import React, { useState, useRef, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import BarcodeScanner from '../BarcodeScanner';
import { authService } from '../../services/authService';
import { enqueue } from '../../services/offlineQueue';
import { tieneFotosDelEquipo } from '../../utils/tecnicoValidacion';
import {
  asegurarCatalogoActualizado, buscarEnCatalogoLocal,
  obtenerProductosFrecuentes, resolverFrecuentesEnCatalogo, ordenarConSugerencias,
} from '../../utils/catalogoLocal';
import { useSugerenciasPieza } from '../../hooks/useSugerenciasPieza';
import { trackEvent, ordenIdAnalytics } from '../../lib/analytics';

// Vista cliente de los estados del servidor (RESULTADO_STATE en
// netlify/functions/finalizarVisita.js) — para actualizar la UI de forma
// optimista, tanto online como cuando la petición se encola sin conexión.
const RESULTADO_STATE = {
  en_ruta: 1018827, cobrada: 1016219, cobrada_sin_firma: 1016219, pendiente_pago: 1007173,
  garantia: 1004686, pendiente_material: 1016222, ausente: 1016220,
  pendiente_presupuesto: 1040205, pendiente_sustitucion: 1016221,
  // Puesta en marcha: Ariston factura/reembolsa esta visita igual que una
  // garantía (decisión de Rafa 2026-10-14) — favorable o no, va directa a
  // Pre-liquidación Ariston. Mismo valor que RESULTADO_STATE en
  // netlify/functions/finalizarVisita.js — mantener sincronizado.
  puesta_marcha: 1041994,
};
const SIGNED_ESTADOS = new Set([
  'cobrada', 'cobrada_sin_firma', 'pendiente_pago', 'garantia',
  'ausente', 'pendiente_material', 'pendiente_presupuesto', 'pendiente_sustitucion',
  'puesta_marcha',
]);

// Checklist de puesta en marcha — transcrito de las hojas oficiales de
// Ariston ("Revisión inicial y activación de la garantía" Nimbus/Caldera,
// 2026-10-14). Los códigos de parámetro de menú (420, 1700...) vienen de
// una foto del papel físico — si alguno no coincide con la hoja real,
// corregir aquí; el técnico sigue teniendo la hoja de Ariston en mano para
// los valores exactos de cada medida, esto es un resumen para dejar
// constancia en la app/StelOrder, no un sustituto del procedimiento oficial.
const CHECKLISTS_PM = {
  aerotermia: {
    label: 'Aerotermia (Nimbus)',
    secciones: [
      { titulo: 'Datos del producto', items: [
        'Modelo y nº de serie registrados',
        'Tipo de servicio anotado (calefacción / agua caliente / refrigeración)',
        'Potencia (kW) anotada',
        'Tipo de elementos emisores anotado (suelo radiante / radiadores baja temperatura / fan-coil / otros)',
      ] },
      { titulo: 'Preliminares unidad externa', items: [
        'Verificación estética del producto',
        'Verificación montaje y distancias a pared y techo',
        'Verificación montaje válvula de seguridad 8 bar y manguitos dieléctricos',
        'Verificación montaje red conexiones hidráulicas con llaves de paso (solo modelo M)',
        'Verificación montaje KIT EXOGEL y toma de condensados (solo modelo M)',
      ] },
      { titulo: 'Preliminares unidad interna', items: [
        'Verificación estética del producto',
        'Verificación distancias de la unidad a pared y techo',
        'Circuito hidráulico lleno, presurizado 1,5 bar y purgado',
        'Comprobación presión vaso de expansión',
        'Verificación montaje red BUS',
      ] },
      { titulo: 'Preliminares acumulador (si lleva)', items: [
        'Verificación estética del producto',
        'Comprobación conexionado Protect',
        'Verificación montaje válvula de seguridad 8 bar y manguitos dieléctricos',
        'Comprobación válvula de seguridad a desagüe',
      ] },
      { titulo: 'Parámetros calefacción — Menú 4, zona 1', items: [
        '420 — Rango de temperatura zona 1',
        '421 — Selección tipo termorregulación',
        '422 — Selección curva termorregulación',
        '423 — Selección desplazamiento paralelo con curva termorregulación',
        '424 — Influencia sonda ambiente sobre termorregulación',
        '425 — Temperatura máxima zona 1 (45°C baja / 60°C alta temperatura)',
        '426 — Temperatura mínima zona 1 (20°C)',
        'Repetido en zonas 2 y 3 si las hay (Menús 5 y 6)',
      ] },
      { titulo: 'Parámetros refrigeración — Menú 1, zona 1 (si aplica)', items: [
        '1740 — Activar modo refrigeración',
        '450 — Temperatura consigna refrigeración zona 1 (7°C fan-coil / 18°C suelo refrescante)',
        '451 — Rango de temperatura zona 1',
        '452 — Selección tipo termorregulación (fan-coil / suelo refrescante)',
        '457 — Temperatura máxima zona 1 (12°C fan-coil / 23°C suelo radiante)',
      ] },
      { titulo: 'Parámetros bomba de calor — Menú 17', items: [
        '1700 — Modo calefacción (Standard / Green / ON)',
        '1704 — ACS habilitado (Off / Plus / Standard)',
        '1720 — Esquema hidráulico (Compact / Flex / Pocket U/Z)',
        '1722 — Modo si 1700=Standard (Eco / Comfort Plus)',
        '1727 — Configurar presencia Protect (Split / Monobloc)',
        '1733 — Control velocidad circulación (solo modelo M, filtros Green)',
        '1750 — Temperatura máxima circuito calefacción (55°C modelo Green)',
        '1752 — Función Comfort ACS',
        '1770-1772 — Modo funcionamiento manual bomba de calor',
        '1780 — Modo Purge (purgado de agua)',
      ] },
      { titulo: 'Cierre', items: [
        'Primer encendido realizado sin errores',
        'Información facilitada al usuario (funcionamiento, mantenimiento, garantía)',
      ] },
    ],
  },
  caldera: {
    label: 'Caldera',
    secciones: [
      { titulo: 'Datos del aparato', items: [
        'Modelo y nº de serie registrados',
        'Termostato anotado (Sensys Net / Cube S NET / TA programable / Sin termostato / Monotubo / Bitubo / Suelo radiante)',
        'Potencia térmica útil nominal (kW) anotada',
        'Tipo de instalación y accesorios registrados',
      ] },
      { titulo: 'Preliminares', items: [
        'Verificación estética del producto',
        'Comprobación conexiones hidráulicas y desagüe',
        'Comprobación materiales, medidas y pendiente de salida de humos',
        'Medida presión de entrada de gas (mbar)',
        'Comprobación tipo de gas y adecuación',
        'Comprobación presión circuito calefacción',
        'Medida tensión de alimentación elementos de regulación',
        'Comprobación conexionado termostato / sonda exterior / sonda ambiente',
        'Medida tensión de alimentación Fase-Neutro',
        'Comprobación tipo de alimentación Neutro-Tierra',
      ] },
      { titulo: 'Primer encendido', items: [
        'Ajuste potencia calefacción (parámetro 231, % del parámetro 234)',
        'Purgado de la instalación (función automática)',
        'Purgado manual del intercambiador principal (modelos condensación)',
        'Verificar secuencia de encendido con demanda de calefacción',
        'Verificar secuencia de encendido con demanda de sanitario',
        'Medida caudal ACS (l/min) y ajuste',
        'Verificar seguridades de la bomba',
        'El local cumple la normativa vigente',
      ] },
      { titulo: 'Regulación válvula de gas (condensación)', items: [
        'Medida valor CO2 a potencia mínima',
        'Medida valor CO2 a potencia máxima (parámetro 220)',
        'Ajuste valor de encendido lento',
        'Medida valor CO ambiente (ppm)',
      ] },
      { titulo: 'Información al usuario', items: [
        'Funcionamiento (selector verano/invierno, confort, Tº consigna, SRA)',
        'Problemas derivados de falta de alimentación de gas',
        'Presión circuito calefacción insuficiente',
        'Fugas de gas / fuera de rango',
        'Restablecimiento de servicio (pulsador reset)',
        'Funcionamiento de conectividad',
        'Información de contratos de mantenimiento',
      ] },
    ],
  },
  // Provisional — pendiente de la hoja oficial de Ariston para NUOS (bomba
  // de calor ACS); usar con reserva hasta confirmarlo.
  bomba_calor: {
    label: 'Bomba de calor (NUOS) — provisional',
    provisional: true,
    secciones: [
      { titulo: 'Datos del producto', items: [
        'Modelo y nº de serie registrados',
        'Potencia (kW) anotada',
      ] },
      { titulo: 'Preliminares', items: [
        'Verificación estética y montaje',
        'Conexión hidráulica al depósito sin fugas',
        'Conexión eléctrica según esquema',
        'Purgado del circuito',
      ] },
      { titulo: 'Primer encendido', items: [
        'Arranque sin errores',
        'Programación de horarios / modo ECO',
        'Prueba de calentamiento de agua',
        'Válvula de seguridad / desagüe comprobados',
      ] },
      { titulo: 'Cierre', items: [
        'Explicación al cliente del manejo',
      ] },
    ],
  },
};

// Detección automática del tipo de equipo a partir del nombre/modelo que ya
// trae la orden (equipmentInfo = asset.name+brand+model de StelOrder) — los
// nombres comerciales (Nimbus/NUOS) son más fiables que las palabras
// genéricas, que no siempre aparecen en el texto de StelOrder.
export function detectarTipoEquipoPM(order) {
  const texto = `${order.equipmentInfo || ''}`.toLowerCase();
  if (/nimbus/.test(texto)) return 'aerotermia';
  if (/nuos/.test(texto)) return 'bomba_calor';
  if (/caldera/.test(texto)) return 'caldera';
  if (/aerotermia/.test(texto)) return 'aerotermia';
  if (/bomba\s*de\s*calor/.test(texto)) return 'bomba_calor';
  return null; // no detectado — el técnico lo elige a mano
}

function construirChecklistItems(tipo) {
  const def = CHECKLISTS_PM[tipo];
  if (!def) return [];
  const items = [];
  def.secciones.forEach((sec, si) => {
    sec.items.forEach((label, ii) => {
      items.push({ id: `${tipo}-${si}-${ii}`, seccion: sec.titulo, label, checked: false });
    });
  });
  return items;
}

function nuevoRequestId() {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch { /* noop */ }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Reduce una foto de móvil (3-8MB) a ~1600px lado largo / JPEG 0.8 antes de
// subirla — mantiene el payload muy por debajo del límite de la función.
function comprimirImagen(file, maxLado = 1600, calidad = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
      const w = Math.round(img.width * escala);
      const h = Math.round(img.height * escala);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', calidad);
      resolve({ base64: dataUrl.split(',')[1], contentType: 'image/jpeg' });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
    img.src = url;
  });
}

// ── Constantes ────────────────────────────────────────────────────────────────

const OUTCOMES = [
  { key: 'cobrada',               emoji: '💰', label: 'Cobrada',               desc: 'Trabajo hecho y cobrado en el acto',         color: 'border-green-300 bg-green-50 text-green-800'   },
  { key: 'cobrada_sin_firma',     emoji: '🖊️', label: 'Cobrada (sin firma)',    desc: 'Cobrado pero cliente no puede/quiere firmar', color: 'border-yellow-300 bg-yellow-50 text-yellow-800'},
  { key: 'pendiente_pago',        emoji: '💳', label: 'Pendiente de pago',      desc: 'Trabajo hecho, pago pendiente',               color: 'border-amber-300 bg-amber-50 text-amber-800'  },
  { key: 'garantia',              emoji: '🛡️', label: 'Garantía',               desc: 'Cubierto por garantía del fabricante',        color: 'border-teal-300 bg-teal-50 text-teal-800'     },
  { key: 'pendiente_material',    emoji: '🔧', label: 'Falta material',         desc: 'Necesita piezas o recambios',                 color: 'border-orange-300 bg-orange-50 text-orange-800'},
  { key: 'ausente',               emoji: '🚪', label: 'Ausente',                desc: 'El cliente no estaba en casa',                color: 'border-gray-300 bg-gray-50 text-gray-700'     },
  { key: 'pendiente_presupuesto', emoji: '📋', label: 'Presupuesto',            desc: 'Requiere presupuesto previo',                 color: 'border-indigo-300 bg-indigo-50 text-indigo-800'},
  { key: 'pendiente_sustitucion', emoji: '🔄', label: 'Sustitución',            desc: 'El equipo debe sustituirse completamente',    color: 'border-purple-300 bg-purple-50 text-purple-800'},
  { key: 'puesta_marcha',         emoji: '🎉', label: 'Puesta en marcha',       desc: 'Onboarding hecho — equipo explicado al cliente', color: 'border-rose-300 bg-rose-50 text-rose-800'   },
];

const MARCAS = [
  { key: 'ariston', label: 'Ariston' },
  { key: 'nofer',   label: 'Nofer' },
  { key: 'svan',    label: 'Svan' },
  { key: 'logman',  label: 'Logman' },
  { key: 'beretta', label: 'Beretta' },
];

// La marca ya es un campo real del activo en StelOrder (asset.brand, ver
// getTechnicianOrders.js → assetBrand) — si no lo tiene, el equipo no está
// de verdad en garantía (corrección de Rafa, 2026-10-15: "es un campo del
// activo que es marca, si no lo tiene no estará en garantía"). Se
// preselecciona sola; el técnico solo tiene que tocar algo si StelOrder no
// la tiene registrada o viene en un texto que no reconocemos.
export function detectarMarcaDelActivo(order) {
  const texto = `${order.assetBrand || ''}`.toLowerCase();
  return MARCAS.find(m => texto.includes(m.key))?.key || '';
}

// Ariston exige nº de serie de 21 dígitos (corrección de Rafa, 2026-10-15) —
// solo aplica al que captura el propio técnico (si StelOrder ya lo tiene
// registrado, se asume correcto, no se vuelve a validar aquí).
export function serieAristonValida(serie) {
  return /^\d{21}$/.test((serie || '').trim());
}

const TIPOS_EQUIPO = [
  'BOMBA DE CALOR', 'AIRE ACONDICIONADO', 'CONDUCTOS',
  'BOMBA DE CALOR CALEFACCION', 'CALDERA', 'TERMO ELECTRICO', 'CALENTADOR DE GAS',
];

// Normaliza (sin acentos, minúsculas) para comparar sin depender de cómo se
// haya escrito el término o el nombre del servicio.
const normalizarTexto = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Esquema StelOrder de `servicios` (name/sales-price) — mismo criterio que
// normServicio() en OrdenesView.js.
function normalizarServicio(s) {
  return { id: s.id, nombre: s.name || '', precio: Number(s['sales-price'] ?? 0) };
}

// Líneas que la orden YA trae de StelOrder (p.ej. el pack de un contrato de
// mantenimiento, cargado por oficina al crear la orden — ver
// createMantenimientoWorkOrder.js). Antes el modal siempre arrancaba en
// blanco (solo el desplazamiento), así que el técnico ni las veía ni las
// tocaba — y el total que se mandaba de vuelta a StelOrder al finalizar
// salía calculado SOLO con lo que el técnico añadía a mano, ignorando por
// completo lo que la orden ya llevaba cargado.
function lineasDeLaOrden(order) {
  const existentes = Array.isArray(order.lines) ? order.lines : [];
  return existentes
    .filter(l => l && (l['item-id'] || l['item-name']))
    .map(l => {
      const cantidad = Number(l.units) || 1;
      const total    = Number(l['total-amount']);
      const precioUnitario = Number(l['item-base-price']) || (Number.isFinite(total) ? total / cantidad : 0);
      return {
        tipo: 'contrato',
        descripcion: l['item-name'] || l.description || 'Línea de la orden',
        cantidad,
        precioUnitario,
        deLaOrden: true,
        // Conserva el id de catálogo real de StelOrder — sin esto,
        // finalizarVisita.js no podía enlazar esta línea de vuelta a su
        // propio item al reenviar el pedido completo de líneas (Rafa,
        // 2026-10-16: StelOrder nunca recibía el desglose).
        itemId: l['item-id'] || null,
      };
    });
}

// Ya no se auto-añade un desplazamiento con precio adivinado por zona — el
// técnico lo elige él mismo de la lista real de servicios (ver
// SelectorServicio más abajo). Esto solo conserva lo que la orden YA trae de
// StelOrder.
function lineasIniciales(order) {
  return lineasDeLaOrden(order);
}

// ── Sub-componentes ───────────────────────────────────────────────────────────

function LineItem({ line, index, onChange, onRemove }) {
  const icons = { desplazamiento: '🚗', mano_obra: '🔨', material: '📦', contrato: '🏷️' };
  return (
    <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
      <span className="text-lg flex-shrink-0">{icons[line.tipo] || '📦'}</span>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={line.descripcion}
            onChange={e => onChange(index, 'descripcion', e.target.value)}
            placeholder="Descripción"
            className="flex-1 min-w-0 text-sm font-medium text-gray-800 bg-transparent border-none outline-none placeholder-gray-400"
          />
          {line.deLaOrden && (
            <span className="flex-shrink-0 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full px-2 py-0.5" title="Ya venía cargada en la orden">
              de la orden
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-400">Cant.</label>
          <input
            type="number"
            min="0.5"
            step="0.5"
            value={line.cantidad}
            onChange={e => onChange(index, 'cantidad', e.target.value)}
            className="w-12 text-xs text-gray-600 bg-white border border-gray-200 rounded px-1.5 py-1 text-center outline-none"
          />
          <label className="text-xs text-gray-400">×</label>
          <div className="relative flex-1">
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={line.precioUnitario}
              onChange={e => onChange(index, 'precioUnitario', e.target.value)}
              className="w-full pl-5 text-xs text-gray-700 bg-white border border-gray-200 rounded px-1.5 py-1 outline-none"
            />
          </div>
          <span className="text-xs font-bold text-gray-700 flex-shrink-0 w-12 text-right">
            €{(Number(line.cantidad) * Number(line.precioUnitario)).toFixed(2)}
          </span>
        </div>
      </div>
      <button data-attr="linea-quitar"
        onClick={() => onRemove(index)}
        className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0"
        title="Eliminar línea"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// Fila de un resultado de búsqueda en el catálogo offline — sin stock (el
// catálogo compacto no lo lleva, se regenera de madrugada; ver
// catalogoLocal.js/generarCatalogoProductos.js), con el precio en su lugar.
// p.nivel ('equipo'|'modelo') viene de ordenarConSugerencias()/las listas de
// sugerencias — resalta las piezas ya usadas en este equipo/modelo (item 3).
function ResultadoProducto({ p, onClick }) {
  const etiquetaNivel = p.nivel === 'equipo' ? 'Usado aquí' : p.nivel === 'modelo' ? 'Usado en este modelo' : null;
  return (
    <button data-attr="material-resultado-elegir"
      onClick={() => onClick(p)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-gray-50 last:border-0 transition-colors ${
        p.nivel ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-blue-50'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-gray-800 truncate">{p.nombre}</p>
          {p.verificada && <span title="Verificada por una garantía ya liquidada">✅</span>}
        </div>
        <p className="text-xs text-gray-400">
          {p.referencia}{p.precioVenta ? ` · €${p.precioVenta}` : ''}
          {etiquetaNivel && (
            <span className={`ml-1.5 font-semibold ${p.nivel === 'equipo' ? 'text-emerald-700' : 'text-teal-700'}`}>
              · {etiquetaNivel}{p.usos ? ` (${p.usos}×)` : ''}
            </span>
          )}
        </p>
      </div>
    </button>
  );
}

// Elegir Desplazamiento/Mano de obra de la lista real de servicios de
// StelOrder, filtrada por una palabra ("desplazamiento"/"mano de obra"), en
// vez de un precio fijo adivinado por zona — Rafa, 2026-10-16: "que se
// tuviera que elegir entre la lista de desplazamientos que hay en
// servicios... y para mano de obra igual". `servicios` es una colección muy
// pequeña (~44, ver OrdenesView.js) — se trae entera una vez por apertura
// del modal, no hace falta un catálogo offline para esto.
function SelectorServicio({ titulo, palabra, servicios, cargando, onSelect, onClose }) {
  const t = normalizarTexto(palabra);
  const filtrados = servicios.filter(s => normalizarTexto(s.nombre).includes(t));
  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={onClose}>
      <div className="w-full bg-white rounded-t-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <p className="font-semibold text-sm text-gray-800">{titulo}</p>
          <button data-attr="servicio-selector-cerrar" onClick={onClose} className="text-gray-400 text-lg leading-none">✕</button>
        </div>
        <div className="overflow-y-auto">
          {cargando ? (
            <p className="text-sm text-gray-400 px-4 py-6 text-center">Cargando servicios…</p>
          ) : filtrados.length === 0 ? (
            <p className="text-sm text-gray-400 px-4 py-6 text-center">Sin servicios que coincidan con "{palabra}"</p>
          ) : (
            filtrados.map(s => (
              <button data-attr="servicio-elegir"
                key={s.id}
                onClick={() => onSelect(s)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-blue-50 text-left border-b border-gray-50 last:border-0 transition-colors"
              >
                <span className="text-sm text-gray-800">{s.nombre}</span>
                <span className="text-sm font-semibold text-gray-600 flex-shrink-0">€{s.precio.toFixed(2)}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// material_añadido — `referencia` es el código de la pieza en el catálogo
// (no un dato del cliente); null en el material manual, cuyo nombre lo
// escribe el propio técnico y no se envía.
// `origen` siempre es sugerida | buscador | manual; las sugerencias por
// equipo/modelo (useSugerenciasPieza) llegan como 'sugerida_equipo'/
// 'sugerida_modelo' y se envían como sugerida + `nivel`.
function registrarMaterial(ordenId, origen, referencia, extra = {}) {
  const [base, nivel] = String(origen).split('_');
  trackEvent('material_añadido', { orden_id: ordenId, referencia: referencia || null, origen: base, ...(nivel ? { nivel } : {}), ...extra });
}

function LineBuilder({ lines, setLines, ordenId, activoId }) {
  const [filteredProds, setFilteredProds] = useState([]);
  const [frecuentes,  setFrecuentes]  = useState([]);
  const [catalogoCargando, setCatalogoCargando] = useState(true);
  const [busqMat,     setBusqMat]     = useState('');
  const [showMatDrop, setShowMatDrop] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  // Único sitio que de verdad escribe en catalogoPiezas (finalizarVisita.js,
  // al cerrar) — aquí solo se leen las sugerencias ya calculadas.
  const { sugerenciasEquipo, sugerenciasModelo, sinSerieValida } = useSugerenciasPieza(activoId);

  // Servicios (Desplazamiento/Mano de obra reales de StelOrder, ver
  // SelectorServicio) — colección pequeña, se trae entera una vez.
  const [servicios, setServicios] = useState([]);
  const [serviciosCargando, setServiciosCargando] = useState(true);
  const [selectorServicio, setSelectorServicio] = useState(null); // null | 'desplazamiento' | 'mano_obra'

  // Catálogo offline (IndexedDB) — se descarga solo si cambió de versión
  // desde la última vez; búsqueda en memoria, sin red, ver catalogoLocal.js.
  useEffect(() => {
    let vivo = true;
    setCatalogoCargando(true);
    asegurarCatalogoActualizado().finally(() => { if (vivo) setCatalogoCargando(false); });
    const employeeId = authService.getCurrentUser()?.employeeId;
    obtenerProductosFrecuentes(employeeId).then(f => { if (vivo) setFrecuentes(f); });
    getDocs(collection(db, 'servicios'))
      .then(snap => { if (vivo) setServicios(snap.docs.map(d => normalizarServicio({ id: d.id, ...d.data() }))); })
      .catch(() => {})
      .finally(() => { if (vivo) setServiciosCargando(false); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    setFilteredProds(buscarEnCatalogoLocal(busqMat, { max: 6 }));
  }, [busqMat]);

  const productosFrecuentesResueltos = resolverFrecuentesEnCatalogo(frecuentes);
  const equipoResueltos = resolverFrecuentesEnCatalogo(sugerenciasEquipo.map(s => ({ referencia: s.referencia, nombre: s.nombre, count: s.usos })));
  const modeloResueltos = resolverFrecuentesEnCatalogo(sugerenciasModelo.map(s => ({ referencia: s.referencia, nombre: s.nombre, count: s.usos })));

  const handleBarcodeScan = (code) => {
    setShowScanner(false);
    const found = buscarEnCatalogoLocal(code, { max: 6 }).find(p => p.ean === code || p.referencia === code);
    if (found) {
      addFromCatalog(found, 'buscador', { escaneado: true });
    } else {
      trackEvent('pieza_no_encontrada', { origen: 'scan' });
      setBusqMat(code);
      setShowMatDrop(true);
    }
  };

  const total = lines.reduce((s, l) => s + Number(l.cantidad) * Number(l.precioUnitario), 0);

  const handleChange = (index, field, value) => {
    setLines(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };
  const handleRemove = (index) => setLines(prev => prev.filter((_, i) => i !== index));

  const addFromCatalog = (prod, origen = 'buscador', extra) => {
    registrarMaterial(ordenId, origen, prod.referencia, extra);
    if (prod.nivel) trackEvent('pieza_sugerida_usada', { nivel: prod.nivel });
    setLines(prev => [...prev, {
      tipo: 'material',
      descripcion: prod.nombre,
      referencia: prod.referencia || '',
      cantidad: 1,
      precioUnitario: prod.precioVenta || 0,
      productoId: prod.id,
    }]);
    setBusqMat('');
    setShowMatDrop(false);
  };

  const addManualMaterial = () => {
    trackEvent('pieza_no_encontrada', { origen: 'manual' });
    registrarMaterial(ordenId, 'manual', null);
    setLines(prev => [...prev, { tipo: 'material', descripcion: '', referencia: '', cantidad: 1, precioUnitario: 0, productoId: null }]);
  };

  // Añade la línea con el nombre/precio REAL del servicio elegido de
  // StelOrder — no un valor fijo adivinado (ver SelectorServicio).
  const agregarServicio = (tipo, s) => {
    setLines(prev => [...prev, { tipo, descripcion: s.nombre, cantidad: 1, precioUnitario: s.precio, productoId: null, servicioId: s.id }]);
    setSelectorServicio(null);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {lines.map((l, i) => (
          <LineItem key={i} line={l} index={i} onChange={handleChange} onRemove={handleRemove} />
        ))}
        {lines.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-2">Sin líneas — añade servicios o materiales</p>
        )}
      </div>

      {/* Botones añadir */}
      <div className="flex flex-wrap gap-2">
        <button data-attr="servicio-anadir-mano-obra"
          onClick={() => setSelectorServicio('mano_obra')}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:border-gray-300 transition"
        >
          🔨 Mano de obra
        </button>
        {!lines.some(l => l.tipo === 'desplazamiento') && (
          <button data-attr="servicio-anadir-desplazamiento"
            onClick={() => setSelectorServicio('desplazamiento')}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:border-gray-300 transition"
          >
            🚗 Desplazamiento
          </button>
        )}
      </div>

      {selectorServicio === 'mano_obra' && (
        <SelectorServicio
          titulo="Mano de obra" palabra="mano de obra tecnica"
          servicios={servicios} cargando={serviciosCargando}
          onSelect={s => agregarServicio('mano_obra', s)}
          onClose={() => setSelectorServicio(null)}
        />
      )}
      {selectorServicio === 'desplazamiento' && (
        <SelectorServicio
          titulo="Desplazamiento" palabra="desplazamiento"
          servicios={servicios} cargando={serviciosCargando}
          onSelect={s => agregarServicio('desplazamiento', s)}
          onClose={() => setSelectorServicio(null)}
        />
      )}

      {/* Buscar material en catálogo */}
      {showScanner && (
        <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} />
      )}
      <div className="relative">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
          <button data-attr="material-escanear"
            onClick={() => setShowScanner(true)}
            className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition"
            title="Escanear código de barras"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </button>
          <input
            type="text"
            value={busqMat}
            onChange={e => { setBusqMat(e.target.value); setShowMatDrop(true); }}
            onFocus={() => setShowMatDrop(true)}
            placeholder="Buscar material o escanear EAN..."
            className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
          />
          <button data-attr="material-anadir-manual"
            onClick={addManualMaterial}
            className="text-xs text-gray-400 hover:text-gray-700 flex-shrink-0 font-medium"
            title="Añadir material manual"
          >
            + manual
          </button>
        </div>

        {showMatDrop && (
          <div className="absolute top-full left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-hidden max-h-80 overflow-y-auto">
            {catalogoCargando ? (
              <p className="text-sm text-gray-400 px-4 py-3">Cargando catálogo…</p>
            ) : busqMat.trim().length < 2 ? (
              equipoResueltos.length > 0 || modeloResueltos.length > 0 || productosFrecuentesResueltos.length > 0 ? (
                <>
                  {equipoResueltos.length > 0 && (
                    <>
                      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider px-4 pt-2.5">Usado en este equipo</p>
                      {equipoResueltos.map(p => (
                        <ResultadoProducto key={`eq-${p.id}`} p={{ ...p, nivel: 'equipo' }} onClick={pr => addFromCatalog(pr, 'sugerida_equipo')} />
                      ))}
                    </>
                  )}
                  {modeloResueltos.length > 0 && (
                    <>
                      <p className="text-xs font-bold text-teal-700 uppercase tracking-wider px-4 pt-2.5">Usado en este modelo</p>
                      {modeloResueltos.map(p => (
                        <ResultadoProducto key={`mo-${p.id}`} p={{ ...p, nivel: 'modelo' }} onClick={pr => addFromCatalog(pr, 'sugerida_modelo')} />
                      ))}
                    </>
                  )}
                  {productosFrecuentesResueltos.length > 0 && (
                    <>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-4 pt-2.5">Tus más usados</p>
                      {productosFrecuentesResueltos.map(p => (
                        <ResultadoProducto key={p.id} p={p} onClick={pr => addFromCatalog(pr, 'sugerida')} />
                      ))}
                    </>
                  )}
                </>
              ) : sinSerieValida ? (
                <p className="text-sm text-gray-400 px-4 py-3">Sin sugerencias: el equipo no tiene un nº de serie válido registrado.</p>
              ) : (
                <p className="text-sm text-gray-400 px-4 py-3">Escribe para buscar, o usa "+ manual"</p>
              )
            ) : filteredProds.length === 0 ? (
              <p className="text-sm text-gray-400 px-4 py-3">Sin resultados — usa "+ manual"</p>
            ) : (
              ordenarConSugerencias(filteredProds, equipoResueltos, modeloResueltos).map(p => <ResultadoProducto key={p.id} p={p} onClick={addFromCatalog} />)
            )}
            <button data-attr="material-buscador-cerrar" onClick={() => setShowMatDrop(false)} className="w-full text-xs text-gray-400 py-2 hover:bg-gray-50 transition-colors">
              Cerrar
            </button>
          </div>
        )}
      </div>

      {/* Total */}
      {lines.length > 0 && (
        <div className="flex items-center justify-between bg-gray-100 rounded-xl px-4 py-3">
          <span className="text-sm font-semibold text-gray-600">Total</span>
          <span className="text-lg font-bold text-gray-900">€{total.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}

// Selector de productos desde el catálogo del almacén. Solo lectura del
// catálogo de piezas por modelo (item 3) — a diferencia de LineBuilder, lo
// que aquí se añade va a "material que falta" (una solicitud para oficina),
// nunca a las líneas facturadas, así que nunca escribe en catalogoPiezas.
function ProductoSelector({ items, setItems, ordenId, activoId }) {
  const [filtered,   setFiltered]   = useState([]);
  const [catalogoCargando, setCatalogoCargando] = useState(true);
  const [busqueda,   setBusqueda]   = useState('');
  const [showDrop,   setShowDrop]   = useState(false);
  const [customText, setCustomText] = useState('');
  const inputRef = useRef(null);
  const { sugerenciasEquipo, sugerenciasModelo, sinSerieValida } = useSugerenciasPieza(activoId);
  const equipoResueltos = resolverFrecuentesEnCatalogo(sugerenciasEquipo.map(s => ({ referencia: s.referencia, nombre: s.nombre, count: s.usos })));
  const modeloResueltos = resolverFrecuentesEnCatalogo(sugerenciasModelo.map(s => ({ referencia: s.referencia, nombre: s.nombre, count: s.usos })));

  // Catálogo offline (IndexedDB) — ver catalogoLocal.js.
  useEffect(() => {
    let vivo = true;
    setCatalogoCargando(true);
    asegurarCatalogoActualizado().finally(() => { if (vivo) setCatalogoCargando(false); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    setFiltered(buscarEnCatalogoLocal(busqueda, { max: 8 }));
  }, [busqueda]);

  const addFromCatalog = (prod, origen = 'buscador') => {
    registrarMaterial(ordenId, origen, prod.referencia);
    if (prod.nivel) trackEvent('pieza_sugerida_usada', { nivel: prod.nivel });
    setItems(prev => [...prev, {
      nombre:     prod.nombre,
      referencia: prod.referencia || '',
      productoId: prod.id,
      cantidad:   1,
    }]);
    setBusqueda('');
    setShowDrop(false);
  };

  const addCustom = () => {
    const t = customText.trim();
    if (!t) return;
    registrarMaterial(ordenId, 'manual', null);
    setItems(prev => [...prev, { nombre: t, referencia: '', productoId: null, stock: null, cantidad: 1 }]);
    setCustomText('');
  };

  const setQty = (i, delta) => setItems(prev => prev.map((it, j) =>
    j === i ? { ...it, cantidad: Math.max(1, (Number(it.cantidad) || 1) + delta) } : it
  ));

  return (
    <div className="space-y-2">
      {/* Items seleccionados */}
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2 bg-orange-50 rounded-xl px-3 py-2.5">
          <span className="text-base flex-shrink-0">{item.productoId ? '📦' : '❓'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">{item.nombre}</p>
            {item.referencia && <p className="text-xs text-gray-400 font-mono">{item.referencia}</p>}
            {!item.productoId && (
              <p className="text-xs text-amber-600">Material no catalogado</p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button data-attr="material-pedido-menos" type="button" onClick={() => setQty(i, -1)}
              className="w-6 h-6 rounded-full bg-white border border-gray-200 text-gray-500 text-sm leading-none">−</button>
            <span className="text-sm font-semibold text-gray-700 w-5 text-center">{Number(item.cantidad) || 1}</span>
            <button data-attr="material-pedido-mas" type="button" onClick={() => setQty(i, 1)}
              className="w-6 h-6 rounded-full bg-white border border-gray-200 text-gray-500 text-sm leading-none">+</button>
          </div>
          <button data-attr="material-pedido-quitar"
            onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}
            className="text-gray-300 hover:text-red-500 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}

      {/* Buscar en catálogo */}
      <div className="relative">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={busqueda}
            onChange={e => { setBusqueda(e.target.value); setShowDrop(true); }}
            onFocus={() => setShowDrop(true)}
            placeholder="Buscar en catálogo (nombre, referencia...)"
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>

        {showDrop && (
          <div className="absolute top-full left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-hidden max-h-80 overflow-y-auto">
            {catalogoCargando ? (
              <p className="text-sm text-gray-400 px-4 py-3">Cargando catálogo…</p>
            ) : busqueda.trim().length < 2 ? (
              <>
                {equipoResueltos.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider px-4 pt-2.5">Usado en este equipo</p>
                    {equipoResueltos.map(p => <ResultadoProducto key={`eq-${p.id}`} p={{ ...p, nivel: 'equipo' }} onClick={pr => addFromCatalog(pr, 'sugerida_equipo')} />)}
                  </>
                )}
                {modeloResueltos.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-teal-700 uppercase tracking-wider px-4 pt-2.5">Usado en este modelo</p>
                    {modeloResueltos.map(p => <ResultadoProducto key={`mo-${p.id}`} p={{ ...p, nivel: 'modelo' }} onClick={pr => addFromCatalog(pr, 'sugerida_modelo')} />)}
                  </>
                )}
                {equipoResueltos.length === 0 && modeloResueltos.length === 0 && (
                  <p className="text-sm text-gray-400 px-4 py-3">
                    {sinSerieValida ? 'Sin sugerencias: el equipo no tiene un nº de serie válido registrado.' : 'Escribe para buscar en catálogo'}
                  </p>
                )}
              </>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 px-4 py-3">Sin resultados en catálogo</p>
            ) : (
              ordenarConSugerencias(filtered, equipoResueltos, modeloResueltos).map(p => <ResultadoProducto key={p.id} p={p} onClick={addFromCatalog} />)
            )}
            <button data-attr="material-pedido-buscador-cerrar"
              onClick={() => setShowDrop(false)}
              className="w-full text-xs text-gray-400 py-2 hover:bg-gray-50 transition-colors"
            >
              Cerrar
            </button>
          </div>
        )}
      </div>

      {/* Añadir material no catalogado */}
      <div className="flex gap-2">
        <input
          type="text"
          value={customText}
          onChange={e => setCustomText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addCustom()}
          placeholder="Material no catalogado (nombre libre)"
          className="flex-1 text-sm bg-gray-50 border border-dashed border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-200"
        />
        <button data-attr="material-pedido-anadir-libre"
          onClick={addCustom}
          disabled={!customText.trim()}
          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-40 text-gray-700 rounded-xl text-sm font-bold transition"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ── Lienzo de firma del cliente ───────────────────────────────────────────────
function SignatureCanvas({ onSign }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const getXY = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const s = e.touches?.[0] ?? e;
    return {
      x: (s.clientX - r.left) * (c.width  / r.width),
      y: (s.clientY - r.top)  * (c.height / r.height),
    };
  };

  const onStart = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getXY(e);
  }, []); // eslint-disable-line

  const onMove = useCallback((e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const c   = canvasRef.current;
    const ctx = c.getContext('2d');
    const pos = getXY(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    lastPos.current = pos;
    onSign(c.toDataURL('image/png'));
  }, [onSign]);

  const onEnd = useCallback(() => {
    drawing.current = false;
    lastPos.current = null;
  }, []);

  const clear = useCallback(() => {
    const c   = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    onSign(null);
  }, [onSign]);

  return (
    <div className="space-y-2">
      <div className="relative select-none">
        <canvas
          ref={canvasRef}
          width={720}
          height={240}
          style={{ touchAction: 'none' }}
          className="w-full rounded-2xl border-2 border-gray-200 bg-white cursor-crosshair"
          onMouseDown={onStart}
          onMouseMove={onMove}
          onMouseUp={onEnd}
          onMouseLeave={onEnd}
          onTouchStart={onStart}
          onTouchMove={onMove}
          onTouchEnd={onEnd}
        />
        <div className="absolute bottom-9 left-6 right-6 border-b border-dashed border-gray-200 pointer-events-none" />
        <p className="absolute bottom-3 right-5 text-xs text-gray-300 pointer-events-none select-none">firma</p>
      </div>
      <button data-attr="firma-borrar"
        type="button"
        onClick={clear}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        Borrar y repetir
      </button>
    </div>
  );
}

// ── Modal principal ───────────────────────────────────────────────────────────

// Outcomes disponibles según el estado de la orden.
//
// 'cobrada_sin_firma' y 'pendiente_pago' YA NO son botones de elección
// inicial (decisión de Rafa, 2026-10-15: "cuantas menos decisiones mejor,
// y el técnico no sabe de antemano si el cliente va a firmar o no").
// Siguen existiendo como resultado interno (RESULTADO_STATE, validate(),
// handleConfirm...) y como forma de reabrir una visita ya cerrada así
// (ESTADO_A_RESULTADO) — solo cambia CÓMO se llega a ellos:
//   - 'cobrada_sin_firma': botón "El cliente no puede/quiere firmar" en el
//     propio paso de firma (se descubre ahí, no antes).
//   - 'pendiente_pago': una 3ª opción de forma de pago dentro de "Cobrada"
//     ("Pendiente de pago"), no una tarjeta aparte.
// "Sustitución" (pendiente_sustitucion) NO es una opción en ruta normal
// (corrección de Rafa, 2026-10-30): fuera de garantía, un equipo a cambiar
// es simplemente un presupuesto de equipo nuevo — ya cubierto por
// 'pendiente_presupuesto'. "Sustitución" es el trámite de garantía donde la
// decisión de cambiar el aparato la toma el fabricante, no el técnico.
const OUTCOMES_EN_RUTA    = OUTCOMES.filter(o => !['garantia', 'puesta_marcha', 'cobrada_sin_firma', 'pendiente_pago', 'pendiente_sustitucion'].includes(o.key));
// Un solo botón "Garantía" — la pregunta "¿está cubierto?" vive dentro de su
// propio formulario; si no está cubierto, cae al mismo flujo de "Cobrada"
// que cualquier otro origen (ver el gate garantiaCubierta más abajo).
const OUTCOMES_GARANTIA   = OUTCOMES.filter(o => ['garantia', 'pendiente_material', 'pendiente_presupuesto', 'pendiente_sustitucion', 'ausente'].includes(o.key));
// Instalación: tampoco tiene sentido "Presupuesto" aquí (ya es un trabajo
// contratado, no uno a presupuestar) — corrección de Rafa, 2026-10-15.
const OUTCOMES_INSTALACION = OUTCOMES.filter(o => ['cobrada', 'pendiente_material', 'ausente'].includes(o.key));
// Puesta en marcha: no hay cobro que gestionar aquí (el equipo ya está
// instalado, esto es onboarding/activación, no una reparación) — por eso no
// tiene sentido Cobrada, Cobrada sin firma, Pendiente de pago, Falta
// material ni Presupuesto (corrección de Rafa, 2026-10-15, probando en
// real). El único comodín real es Ausente (no había nadie en casa);
// el resultado normal es confirmarla.
const OUTCOMES_PM = OUTCOMES.filter(o => ['puesta_marcha', 'ausente'].includes(o.key));

// Priorización visual para visitas de un contrato de mantenimiento (ver
// esVisitaDeMantenimiento más abajo): la gran mayoría termina en una de estas
// tres — el resto son casos reales pero minoritarios que no hace falta
// mostrar con el mismo peso por defecto (quedan bajo "Ver más opciones").
const OUTCOMES_MANTENIMIENTO_PRINCIPALES = new Set(['cobrada', 'ausente', 'pendiente_material']);

// Al reabrir el modal para CORREGIR una visita que ya se finalizó (ver
// "Corregir visita" en TechnicianOrderDetailView.js), order.estado ya es un
// resultado real guardado por una pasada anterior — se usa para saltar
// directo al paso de detalles con ese resultado preseleccionado, en vez de
// obligar a repetir la elección desde cero.
const ESTADO_A_RESULTADO = {
  cobrada: 'cobrada', cobrada_sin_firma: 'cobrada_sin_firma', pendiente_pago: 'pendiente_pago',
  ausente: 'ausente', pendiente_material: 'pendiente_material',
  pendiente_presupuesto: 'pendiente_presupuesto', pendiente_sustitucion: 'pendiente_sustitucion',
  puesta_marcha: 'puesta_marcha',
};
// visita_resultado_elegido — agrupa los resultados de la app en las opciones
// de decisión del análisis; el resultado exacto va aparte (`resultado`).
// ausente y presupuesto → no_se_cobra (no hay trabajo que cobrar; si en
// presupuesto se cobra la visita, se ve en cobro_metodo).
const OPCION_DECISION = {
  cobrada: 'trabajo_cobrado', cobrada_sin_firma: 'trabajo_cobrado', pendiente_pago: 'trabajo_cobrado',
  pendiente_material: 'necesita_piezas', garantia: 'garantia',
  ausente: 'no_se_cobra', pendiente_presupuesto: 'no_se_cobra',
};

function resultadoDesdeEstado(estado) {
  if (typeof estado !== 'string') return null;
  if (ESTADO_A_RESULTADO[estado]) return ESTADO_A_RESULTADO[estado];
  if (estado.startsWith('garantia_')) return 'garantia';
  return null;
}
// 156786 = "TARJETA" en el catálogo real de formas de pago de StelOrder
// (GET /app/paymentOptions) — 53267 no existía ahí, StelOrder lo mostraba
// como "Cheque" (Rafa, 2026-10-16).
const PAYMENT_ID_A_FORMA = { 156786: 'tarjeta', 53268: 'efectivo' };

// Botón de resultado del paso 1 — un solo sitio para el principal y el
// desplegable de "otras opciones" (ver outcomesPrincipales/Secundarias).
function OutcomeButton({ outcome: o, onSelect }) {
  return (
    <button data-attr={`resultado-${o.key}`}
      onClick={() => onSelect(o.key)}
      className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all active:scale-[0.98] ${o.color}`}
    >
      <span className="text-2xl leading-none flex-shrink-0">{o.emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{o.label}</p>
        <p className="text-xs opacity-70">{o.desc}</p>
      </div>
      <svg className="w-4 h-4 opacity-50 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

// Qué resultados puede elegir el técnico según el estado de ORIGEN de la
// orden — pura, exportada para poder probarla sin montar el modal entero
// (mismo criterio que validateOrders()/computeCompletitud en
// TechnicianOrdersView.js).
export function getBaseOutcomes(order) {
  const isGarantia      = order['document-state-id'] === 1004686;
  const isInstalacion   = order['document-state-id'] === 1305009 || order.tipo === 'instalacion' || order.estado === 'instalacion';
  const isPuestaMarcha  = order['document-state-id'] === 1069430;
  return isGarantia ? OUTCOMES_GARANTIA
    : isInstalacion ? OUTCOMES_INSTALACION
    : isPuestaMarcha ? OUTCOMES_PM
    : OUTCOMES_EN_RUTA;
}

export default function FinalizarVisitaModal({ order, onClose, onSuccess }) {
  const isGarantia      = order['document-state-id'] === 1004686;
  const isInstalacion   = order['document-state-id'] === 1305009 || order.tipo === 'instalacion' || order.estado === 'instalacion';
  const baseOutcomes    = getBaseOutcomes(order);

  // Contrato de mantenimiento ya domiciliado por SEPA (getTechnicianOrders.js
  // lo enriquece cruzando con la colección mantenimientos): "Cobrada" no
  // debería significar "cobro en efectivo/tarjeta" aquí, porque el cargo real
  // ya lo hace el banco — eso es justo lo que antes solo se avisaba con un
  // aviso rojo DESPUÉS de que el técnico ya hubiera elegido forma de pago.
  // Ahora se cambia la etiqueta y se oculta esa decisión por defecto (ver
  // más abajo, bloque COBRADA), no solo se advierte.
  const esMantenimientoSepa = order.mantenimientoModoCobro === 'sepa';
  const availableOutcomes = baseOutcomes.map(o => {
    if (!esMantenimientoSepa) return o;
    if (o.key === 'cobrada')
      return { ...o, emoji: '✅', label: 'Mantenimiento realizado', desc: 'El cobro ya es por domiciliación SEPA' };
    if (o.key === 'cobrada_sin_firma')
      return { ...o, desc: 'El cobro ya es por domiciliación SEPA — solo falta la firma' };
    return o;
  });

  // Priorización visual (no elimina ninguna opción, solo reordena qué se ve
  // de entrada): en un SAT cualquier resultado es igual de probable, así que
  // ahí se dejan las 7 opciones tal cual. En una visita de un contrato de
  // mantenimiento (mantenimientoModoCobro viene de getTechnicianOrders.js
  // cruzando con la colección mantenimientos — visita o SEPA, da igual) la
  // gran mayoría termina en una de estas tres; el resto son casos reales pero
  // minoritarios que no hace falta mostrar con el mismo peso por defecto.
  const esVisitaDeMantenimiento = baseOutcomes === OUTCOMES_EN_RUTA && !!order.mantenimientoModoCobro;
  const outcomesPrincipales = esVisitaDeMantenimiento
    ? availableOutcomes.filter(o => OUTCOMES_MANTENIMIENTO_PRINCIPALES.has(o.key))
    : availableOutcomes;
  const outcomesSecundarias = esVisitaDeMantenimiento
    ? availableOutcomes.filter(o => !OUTCOMES_MANTENIMIENTO_PRINCIPALES.has(o.key))
    : [];

  // Si la orden ya trae un resultado guardado, esto es una corrección — se
  // precarga todo lo de abajo con lo que ya se había guardado.
  const resultadoPrevio = resultadoDesdeEstado(order.estado);
  const esCorreccion    = !!resultadoPrevio;

  const [step,         setStep]         = useState(resultadoPrevio ? 'details' : 'outcome'); // 'outcome' | 'details' | 'firma'
  // "Ver más opciones" en el paso 1 (solo aplica cuando hay opciones
  // secundarias, ver outcomesSecundarias) — si la corrección ya viene con un
  // resultado de las secundarias, se abre desplegado directamente.
  const [mostrarMasOpciones, setMostrarMasOpciones] = useState(() =>
    !!resultadoPrevio && !OUTCOMES_MANTENIMIENTO_PRINCIPALES.has(resultadoPrevio));
  const [outcome,      setOutcome]      = useState(resultadoPrevio);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  // Un id estable por apertura del modal — dedupe en el servidor si la
  // bandeja offline reintenta una respuesta perdida. En una corrección es a
  // propósito un id NUEVO (no el de la primera vez): es un envío distinto,
  // con datos corregidos, no un reintento de lo mismo.
  const requestIdRef = useRef(null);
  if (!requestIdRef.current) requestIdRef.current = nuevoRequestId();

  // Momento de apertura del modal — base para la duración de orden_cerrada.
  const aberturaRef = useRef(Date.now());
  // orden_id en todos los eventos de esta orden — ver ordenIdAnalytics().
  const ordenId = ordenIdAnalytics(order);
  useEffect(() => {
    trackEvent('orden_abierta', { orden_id: ordenId, esCorreccion });
  }, []); // eslint-disable-line

  // Tiempo de visita — esta pantalla se rellena al ACABAR la visita, así que
  // "ahora" es una buena hora de fin por defecto (editable); la de llegada
  // ya pasó, así que la introduce el técnico a mano, sin adivinarla.
  const [horaLlegada,  setHoraLlegada]  = useState(() => order.horaLlegada || '');
  const [horaFin,      setHoraFin]      = useState(() => order.horaFin || new Date().toTimeString().slice(0,5));

  // Cobrada sin firma
  const [motivoSinFirma, setMotivoSinFirma] = useState(order.motivoSinFirma || '');

  // Cobrada / Pendiente pago — si se está corrigiendo, las líneas que ya se
  // habían guardado (order.lineas) tienen prioridad sobre las de StelOrder;
  // si no, arranca con lo que la orden ya trae de StelOrder (lineasIniciales),
  // no solo el desplazamiento.
  const [lines,        setLines]        = useState(() =>
    Array.isArray(order.lineas) && order.lineas.length > 0 ? order.lineas : lineasIniciales(order));
  const [formaPago,    setFormaPago]    = useState(() => PAYMENT_ID_A_FORMA[order['payment-option-id']] || 'efectivo');
  const [justificacion, setJustificacion] = useState(order.justificacion || '');

  // Garantía
  const [marcaKey,     setMarcaKey]     = useState(() =>
    typeof order.estado === 'string' && order.estado.startsWith('garantia_') ? order.estado.slice('garantia_'.length) : detectarMarcaDelActivo(order));
  const [notas,        setNotas]        = useState(resultadoPrevio === 'garantia' ? (order.observaciones || '') : '');
  // ¿Está cubierto por la garantía? null = sin responder todavía (se
  // pregunta antes de nada al entrar en el formulario de Garantía) — true
  // mantiene el flujo de garantía de siempre; false cae al mismo flujo de
  // "Cobrada" que cualquier otro cobro (ver handleSelectOutcome/
  // handleGarantiaNoCubierta). Al corregir una garantía ya guardada, se da
  // por hecho que sí estaba cubierta (así se guardó).
  const [garantiaCubierta, setGarantiaCubierta] = useState(resultadoPrevio === 'garantia' ? true : null);

  // Ausente / presupuesto notas
  const [notasGeneral, setNotasGeneral] = useState(resultadoPrevio && resultadoPrevio !== 'garantia' ? (order.observaciones || '') : '');

  // Falta material
  const [materialItems, setMaterialItems] = useState(() => Array.isArray(order.materialFaltante) ? order.materialFaltante : []);

  // Puesta en marcha — si se ofreció el contrato de mantenimiento anual y qué
  // respondió el cliente. La CREACIÓN del contrato (enlace de firma) es un
  // flujo aparte, ya existente en TechnicianOrderDetailView.js ("Crear
  // contrato de mantenimiento"); esto solo registra el resultado de la
  // conversación para que oficina no tenga que repasar cada PM a mano.
  const [contratoMantenimientoResultado, setContratoMantenimientoResultado] = useState(order.contratoMantenimientoResultado || null); // null | 'aceptado' | 'rechazado' | 'no_ofrecido'

  // Checklist de puesta en marcha — paso previo a las notas de onboarding,
  // solo para outcome 'puesta_marcha' (ver CHECKLISTS_PM arriba). El tipo se
  // detecta solo del equipo; si no se puede, el técnico lo elige a mano.
  const [checklistTipo, setChecklistTipo] = useState(() =>
    order.checklistPuestaMarcha?.tipo || detectarTipoEquipoPM(order));
  const [checklistItems, setChecklistItems] = useState(() =>
    Array.isArray(order.checklistPuestaMarcha?.items) && order.checklistPuestaMarcha.items.length
      ? order.checklistPuestaMarcha.items
      : construirChecklistItems(order.checklistPuestaMarcha?.tipo || detectarTipoEquipoPM(order)));
  const [checklistFavorable, setChecklistFavorable] = useState(
    order.checklistPuestaMarcha?.favorable ?? null); // null | true | false

  // Fotos de la visita (subidas a Storage; se adjuntan URLs a la orden) — al
  // corregir, las fotos ya subidas antes se mantienen visibles (no hace
  // falta repetir la foto para cambiar solo una línea o el DNI).
  const [fotos, setFotos]         = useState(() => Array.isArray(order.fotos) ? order.fotos.map(url => ({ url })) : []);
  const [subiendoFotos, setSubiendo] = useState(0); // nº de subidas en curso
  const [fotoError, setFotoError] = useState('');

  // Presupuesto
  const [cobrarVisita,  setCobrarVisita] = useState(order.cobrarVisita ?? null); // null | true | false
  const [visitaLines,   setVisitaLines]  = useState(() =>
    Array.isArray(order.lineas) && order.lineas.length > 0 ? order.lineas : []);
  const [visitaFormaPago, setVisitaFormaPago] = useState(() => PAYMENT_ID_A_FORMA[order['payment-option-id']] || 'efectivo');

  // DNI/NIE solo obligatorio si se cobra más de 1000€ o es una instalación —
  // en el resto de visitas pedirlo siempre era fricción sin necesidad real
  // (Rafa, 2026-10-16). 'pendiente_presupuesto' con cobrarVisita=true usa
  // visitaLines, el resto de outcomes que llegan a firma usan lines.
  const lineasParaTotal = outcome === 'pendiente_presupuesto' ? visitaLines : lines;
  const totalActual = lineasParaTotal.reduce((s, l) => s + (Number(l.cantidad) || 1) * (Number(l.precioUnitario) || 0), 0);
  const requiereDni = isInstalacion || totalActual > 1000;

  // Sustitución — nuevo activo
  const nuevoActivoPrevio = order.nuevoActivo || {};
  const [nuevoMarca,   setNuevoMarca]   = useState(nuevoActivoPrevio.marca || '');
  const [nuevoModelo,  setNuevoModelo]  = useState(nuevoActivoPrevio.modelo || '');
  const [nuevoSerie,   setNuevoSerie]   = useState(nuevoActivoPrevio.numeroSerie || '');
  const [nuevoTipo,    setNuevoTipo]    = useState(nuevoActivoPrevio.tipoEquipo || '');
  const [nuevoNotas,   setNuevoNotas]   = useState(nuevoActivoPrevio.notas || '');

  // Firma del cliente
  const [firmaBase64,  setFirmaBase64]  = useState(null);
  // Precargado si ya se conoce (order.clienteDni, o el de la propia orden si
  // se está corrigiendo una visita ya finalizada) — editable, pero ya no
  // vacío por defecto en un cliente recurrente.
  const [dniCliente,   setDniCliente]   = useState((order.dniCliente || order.clienteDni || '').toUpperCase());

  // Mantenimiento SEPA: por defecto no se pregunta forma de pago (nada que
  // cobrar en la visita). Este interruptor es la única forma de que aparezca
  // — para el caso real pero minoritario de un trabajo aparte no incluido en
  // el contrato (p.ej. una reparación adicional que sí se cobra in situ).
  const [cobroExtra,   setCobroExtra]   = useState(false);

  // Garantía — nº de serie del equipo YA existente (distinto del de
  // "Sustitución", que es de un equipo NUEVO). Solo hace falta pedirlo si
  // StelOrder todavía no lo tiene — si ya viene relleno, no se vuelve a pedir.
  const [numeroSerieCapturado, setNumeroSerieCapturado] = useState(order.numeroSerieCapturado || '');
  const [showSerieScanner, setShowSerieScanner] = useState(false);
  // 'manual' | 'scan' | null — para no disparar serie_tecleado cuando el
  // valor vino de escanear (ver el input y BarcodeScanner más abajo).
  const serieOrigenRef = useRef(null);
  const faltaSerieGarantia = outcome === 'garantia' && !order.assetSerialNumber;

  const selectedOutcome = OUTCOMES.find(o => o.key === outcome);
  // Mismo criterio que validateOrders() (TechnicianOrdersView.js) — StelOrder
  // ya tiene fotos (assetHasImages) O el técnico añadió alguna en esta misma
  // visita (fotos, que incluye tanto las ya guardadas como las recién
  // subidas). Una sola fuente de verdad para no repetir el bug de que las
  // dos validaciones (aquí y en el cierre de ruta) exigieran cosas distintas.
  const yaTieneFotosDelEquipo = tieneFotosDelEquipo({ assetHasImages: order.assetHasImages, fotos: fotos.map(f => f.url) });

  const handleSelectOutcome = (key) => {
    trackEvent('visita_resultado_elegido', { orden_id: ordenId, opcion: OPCION_DECISION[key] || 'otro', resultado: key });
    setOutcome(key);
    setError('');
    // Cada vez que se entra (o se reentra) a Garantía se vuelve a preguntar
    // cobertura desde cero — si el técnico vuelve atrás y la toca otra vez
    // no debe arrastrar la respuesta de la vez anterior.
    if (key === 'garantia') setGarantiaCubierta(resultadoPrevio === 'garantia' ? true : null);
    // Puesta en marcha confirmada pasa primero por el checklist técnico del
    // equipo (favorable o no) — las notas de onboarding/contrato van DESPUÉS,
    // no en vez de. El resto de resultados van directos a 'details' como
    // siempre.
    setStep(key === 'puesta_marcha' ? 'checklist' : 'details');
  };

  // Garantía → "No cubierto, el cliente debe abonar la visita": cae al mismo
  // flujo de Cobrada que cualquier otro cobro (líneas, forma de pago, firma
  // con su propio escape de "no firma") — no un botón "Cobrada" aparte en la
  // lista de Garantía (decisión de Rafa, 2026-10-15: menos decisiones para
  // el técnico). Se precarga una nota para que quede trazado el motivo.
  const handleGarantiaNoCubierta = () => {
    trackEvent('garantia_respuesta', { orden_id: ordenId, cubierto: 'no' });
    setGarantiaCubierta(false);
    setOutcome('cobrada');
    setNotasGeneral(prev => prev || 'Garantía no cubierta — ');
  };

  // cobro_metodo — al elegirlo (no al enviar), para ver la decisión aunque
  // luego se abandone. Solo si cambia: volver a tocar el mismo no cuenta.
  // "Pendiente" (pago luego, lo gestiona oficina) se registra como
  // transferencia; "No se cobra" de la visita, como no_se_cobra.
  const metodoCobroRef = useRef(null);
  const registrarCobro = (metodo) => {
    if (metodoCobroRef.current === metodo) return;
    metodoCobroRef.current = metodo;
    trackEvent('cobro_metodo', { orden_id: ordenId, metodo });
  };

  const handleBack = () => {
    if (step === 'firma') {
      setStep('details');
    } else if (step === 'details' && outcome === 'puesta_marcha') {
      setStep('checklist');
    } else {
      setStep('outcome');
    }
    setError('');
  };

  // Cerrar por accidente (tocar el fondo oscuro, fácil con el móvil en una
  // mano) perdía todo lo rellenado sin avisar — líneas de material, notas,
  // fotos ya elegidas — en la pantalla donde más tiempo se invierte de toda
  // la app. Solo se confirma si ya se ha avanzado más allá de elegir el
  // resultado (step==='outcome' no tiene nada que perder todavía).
  const handleRequestClose = () => {
    if (step !== 'outcome' && !window.confirm('¿Seguro que quieres salir? Se perderá lo que has rellenado en esta visita.')) return;
    if (step !== 'outcome') trackEvent('orden_abandonada', { orden_id: ordenId, step, outcome: outcome || null });
    onClose();
  };

  const handleFotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setFotoError('Sin conexión — podrás añadir las fotos cuando recuperes cobertura.');
      return;
    }
    setFotoError('');
    let alguna = false;
    for (const file of files.slice(0, 8)) {
      if (!file.type.startsWith('image/')) continue;
      setSubiendo(n => n + 1);
      try {
        const { base64, contentType } = await comprimirImagen(file);
        const resp = await fetch('/.netlify/functions/subirFotoVisita', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ fileBase64: base64, contentType, ordenId: order._firebaseId || order.id }),
        });
        const data = await resp.json();
        if (data.success && data.url) { setFotos(prev => [...prev, { url: data.url }]); alguna = true; }
        else setFotoError(data.error || 'No se pudo subir una foto');
      } catch (err) {
        setFotoError(err.message || 'No se pudo subir una foto');
      } finally {
        setSubiendo(n => n - 1);
      }
    }
    if (alguna) trackEvent('orden_paso_completado', { orden_id: ordenId, paso: 'fotos' });
  };

  // Varios requisitos a la vez → una lista, no un único mensaje — ver
  // comentario junto a validate().
  const formatearFaltantes = (faltan) => faltan.length === 1 ? faltan[0] : faltan.map(f => `• ${f}`).join('\n');

  // Avanzar al paso de firma (o confirmar directamente para algunos outcomes)
  const handleGoToFirma = () => {
    const validationError = validate();
    if (validationError) { setError(formatearFaltantes(validationError)); return; }
    // Estos outcomes no requieren paso de firma — "Falta material" solo lo
    // salta cuando no se cobra la visita de hoy; si se cobra, pasa por firma
    // igual que cualquier otro cobro.
    if (outcome === 'ausente' || outcome === 'cobrada_sin_firma' ||
        (outcome === 'pendiente_material' && !cobrarVisita) || outcome === 'pendiente_sustitucion' ||
        outcome === 'garantia' || outcome === 'puesta_marcha' ||
        (outcome === 'pendiente_presupuesto' && !cobrarVisita)) {
      handleConfirm(null);
      return;
    }
    setError('');
    setFirmaBase64(null);
    // Antes se vaciaba aquí el DNI justo al entrar en este paso — anulaba el
    // prellenado de un cliente recurrente (ver el comentario junto al estado
    // dniCliente, "ya no vacío por defecto") en cuanto el técnico llegaba a
    // la firma, que es precisamente cuándo hace falta tenerlo a mano.
    setStep('firma');
  };

  // ── Validación por tipo ──────────────────────────────────────────────────────
  // Estas reglas están alineadas 1:1 con validateOrders() (TechnicianOrdersView.js),
  // que es lo que de verdad bloquea el cierre de ruta al final del día. Antes
  // varios de estos requisitos no se pedían aquí — el técnico solo se enteraba
  // de que faltaban horas después, al cerrar la ruta, cuando ya no estaba
  // delante del cliente para conseguirlos (fotos, nº de serie...).
  // Devuelve TODO lo que falta de golpe, no solo lo primero — antes el
  // técnico corregía un campo, pulsaba "Continuar", y recién entonces se
  // enteraba del segundo requisito que también le faltaba (y luego del
  // tercero...). Con varios requisitos a la vez (p. ej. "cobrada" pide
  // notas Y líneas) era un goteo de intentos en vez de una sola lista.
  const validate = () => {
    const faltan = [];
    if (outcome === 'garantia' && !marcaKey)
      faltan.push('Selecciona la marca del equipo');
    // assetHasImages: StelOrder ya tiene documentación real de este equipo
    // (sincronizada vía activoImagenes) — no hace falta repetir la foto
    // ahora mismo. Antes solo se miraba `fotos` (esta visita), así que una
    // orden con fotos de verdad en StelOrder bloqueaba igual aquí aunque el
    // cierre de ruta (validateOrders(), que sí mira las dos fuentes) no lo
    // habría exigido — paso duplicado sin motivo real.
    if (outcome === 'garantia' && !yaTieneFotosDelEquipo)
      faltan.push('Añade al menos una foto del equipo — obligatoria para tramitar la garantía con el fabricante');
    if (outcome === 'garantia' && faltaSerieGarantia && !numeroSerieCapturado.trim())
      faltan.push('Indica el número de serie del equipo — obligatorio para tramitar la garantía (StelOrder no lo tiene registrado)');
    if (outcome === 'garantia' && faltaSerieGarantia && marcaKey === 'ariston' && numeroSerieCapturado.trim() && !serieAristonValida(numeroSerieCapturado))
      faltan.push('El número de serie de Ariston tiene que tener 21 dígitos — revísalo');
    if (outcome === 'ausente' && !notasGeneral.trim())
      faltan.push('Añade una observación (ej: nadie en casa, dejé aviso...)');
    if (outcome === 'pendiente_pago' && !justificacion.trim())
      faltan.push('La justificación es obligatoria para pendiente de pago');
    if (outcome === 'pendiente_pago' && lines.length === 0)
      faltan.push('Añade al menos una línea de servicio o material — oficina no podrá facturarlo sin esto');
    if ((outcome === 'cobrada' || outcome === 'cobrada_sin_firma') && lines.length === 0)
      faltan.push('Añade al menos una línea de servicio o material — oficina no podrá facturarlo sin esto');
    if (outcome === 'cobrada' && !notasGeneral.trim())
      faltan.push('Describe el trabajo realizado — obligatorio para dejarlo registrado');
    if (outcome === 'pendiente_material' && materialItems.length === 0)
      faltan.push('Añade al menos un material que falta');
    // Fuera de garantía, "Falta material" también pregunta si se cobra la
    // visita de hoy — una garantía nunca la paga el cliente, así que ahí no
    // se pregunta (ver el bloque condicional en el render).
    if (outcome === 'pendiente_material' && !isGarantia && cobrarVisita === null)
      faltan.push('Indica si se cobra la visita');
    if (outcome === 'pendiente_material' && !isGarantia && cobrarVisita === true && lines.length === 0)
      faltan.push('Añade al menos una línea de servicio o material — oficina no podrá facturarlo sin esto');
    if (outcome === 'pendiente_presupuesto' && cobrarVisita === null)
      faltan.push('Indica si se cobra la visita');
    if (outcome === 'pendiente_presupuesto' && cobrarVisita === false && !notasGeneral.trim())
      faltan.push('Describe el trabajo a presupuestar (obligatorio para el cierre de ruta)');
    // cobrarVisita=true se envía como 'cobrada' de verdad (ver handleConfirm) —
    // mismo requisito de líneas que un cobro normal.
    if (outcome === 'pendiente_presupuesto' && cobrarVisita === true && visitaLines.length === 0)
      faltan.push('Añade al menos una línea de servicio o material — oficina no podrá facturarlo sin esto');
    if (outcome === 'pendiente_sustitucion' && (!nuevoMarca.trim() || !nuevoModelo.trim()))
      faltan.push('Marca y modelo del nuevo equipo son obligatorios');
    if (outcome === 'pendiente_sustitucion' && !yaTieneFotosDelEquipo)
      faltan.push('Añade al menos una foto del equipo antiguo — obligatoria para justificar la sustitución');
    if (outcome === 'cobrada_sin_firma' && !motivoSinFirma.trim())
      faltan.push('Indica el motivo por el que no hay firma del cliente');
    if (outcome === 'puesta_marcha' && !notasGeneral.trim())
      faltan.push('Describe el onboarding realizado (equipo explicado, dudas resueltas...)');
    if (outcome === 'puesta_marcha' && !contratoMantenimientoResultado)
      faltan.push('Indica si el cliente aceptó el contrato de mantenimiento');
    if (outcome === 'puesta_marcha' && checklistFavorable === null)
      faltan.push('Vuelve al checklist e indica si la puesta en marcha queda favorable o no');
    return faltan.length ? faltan : null;
  };

  // ── Envío ────────────────────────────────────────────────────────────────────
  // firmaParaEnviar: undefined → usa el estado firmaBase64; null → sin firma (skip)
  const handleConfirm = async (firmaParaEnviar = undefined) => {
    const validationError = validate();
    if (validationError) { setError(formatearFaltantes(validationError)); return; }
    const firmaToSend = firmaParaEnviar !== undefined ? firmaParaEnviar : firmaBase64;
    // Red de seguridad por si el técnico la borra sin querer — horaFin ya
    // viene autocompletada con la hora actual al abrir el modal.
    const horaFinFinal = horaFin || new Date().toTimeString().slice(0, 5);

    setSaving(true);
    setError('');

    // Determinar el resultado efectivo
    let resultado = outcome;
    let lineasToSend = null;
    let formaPagoToSend = null;

    // Mantenimiento SEPA sin cobro aparte: no se manda forma de pago — nada
    // que registrar como cobro en efectivo/tarjeta, el cargo real lo hace el
    // banco. Si el técnico marcó "he cobrado algo aparte", se manda igual que
    // cualquier otro cobro normal.
    const sinPagoEnVisita = esMantenimientoSepa && !cobroExtra;

    if (outcome === 'cobrada') {
      lineasToSend    = lines;
      formaPagoToSend = sinPagoEnVisita ? null : formaPago;
    } else if (outcome === 'cobrada_sin_firma') {
      // Se trata como cobrada pero sin firma — la oficina verifica
      resultado       = 'cobrada_sin_firma';
      lineasToSend    = lines;
      formaPagoToSend = sinPagoEnVisita ? null : formaPago;
    } else if (outcome === 'pendiente_pago') {
      lineasToSend = lines;
    } else if (outcome === 'pendiente_presupuesto' && cobrarVisita) {
      resultado       = 'cobrada';
      lineasToSend    = visitaLines;
      formaPagoToSend = visitaFormaPago;
    } else if (outcome === 'pendiente_material' && cobrarVisita) {
      // Igual que "Presupuesto": si se cobra la visita de hoy (desplazamiento/
      // diagnóstico), la orden pasa a 'cobrada' de verdad — con eso factura
      // normal, email al cliente, todo el camino habitual de un cobro. La
      // segunda visita (con la pieza) queda registrada en una orden NUEVA que
      // crea el backend (ver crearOrdenPendienteMaterial en finalizarVisita.js),
      // no en esta misma — antes, al dejar esta orden en 'pendiente_material',
      // el cobro de hoy no se facturaba hasta que se resolvía la segunda
      // visita, semanas después (Rafa, 2026-10-30).
      resultado       = 'cobrada';
      lineasToSend    = lines;
      formaPagoToSend = formaPago;
    } else if (outcome === 'garantia') {
      // No se manda formaPago — el backend ya pone payment-option-id a
      // PAYMENT_WARRANTY para resultado==='garantia' (lo paga/reembolsa el
      // fabricante, no el cliente). Las líneas son las mismas que después
      // usa crearAlbaranAriston.js al liquidar.
      lineasToSend = lines;
    }

    // "Materiales" y "cobro" no tienen un paso propio en esta pantalla (se
    // rellenan junto al resto en 'details' y se envían todos de golpe aquí)
    // — se registran en el mismo instante que orden_cerrada, no antes.
    if (outcome === 'pendiente_material' && materialItems.length > 0)
      trackEvent('orden_paso_completado', { orden_id: ordenId, paso: 'materiales' });
    if (Array.isArray(lineasToSend) && lineasToSend.length > 0 && formaPagoToSend)
      trackEvent('orden_paso_completado', { orden_id: ordenId, paso: 'cobro' });

    // La firma se sube a Storage igual que las fotos (mismo endpoint) — antes
    // viajaba siempre como base64 embebido en el propio documento de la
    // orden, y ese campo se reenviaba entero cada vez que el técnico
    // refrescaba la lista del día. Si la subida falla (sin conexión, por
    // ejemplo), se manda el base64 igual como reserva — no se pierde la
    // firma por esto.
    let firmaUrl = null;
    let firmaBase64ParaEnviar = firmaToSend || null;
    if (firmaToSend && typeof navigator !== 'undefined' && navigator.onLine !== false) {
      try {
        const resp = await fetch('/.netlify/functions/subirFotoVisita', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ fileBase64: firmaToSend.split(',')[1], contentType: 'image/png', ordenId: order._firebaseId || order.id }),
        });
        const data = await resp.json();
        if (data.success && data.url) { firmaUrl = data.url; firmaBase64ParaEnviar = null; }
      } catch { /* sigue con el base64 de reserva */ }
    }

    const payload = {
      firebaseId:      order._firebaseId || String(order.id),
      stelorderId:     order.stelorderId || order.id,
      resultado,
      lineas:          lineasToSend,
      formaPago:       formaPagoToSend,
      justificacion:   justificacion || null,
      observaciones:   outcome === 'garantia' ? notas || null : notasGeneral || null,
      materialFaltante: outcome === 'pendiente_material' ? materialItems : null,
      marcaKey:        outcome === 'garantia' ? marcaKey : null,
      numeroSerieCapturado: (outcome === 'garantia' && faltaSerieGarantia) ? numeroSerieCapturado.trim() || null : null,
      nuevoActivo:     outcome === 'pendiente_sustitucion' ? {
        marca: nuevoMarca, modelo: nuevoModelo, numeroSerie: nuevoSerie || null,
        tipoEquipo: nuevoTipo || null, notas: nuevoNotas || null,
      } : null,
      cobrarVisita:    (outcome === 'pendiente_presupuesto' || outcome === 'pendiente_material') ? cobrarVisita : null,
      firmaUrl,
      firmaBase64:     firmaBase64ParaEnviar,
      // Antes solo se mandaba si había firma — un técnico que tecleaba el DNI
      // y luego pulsaba "Continuar sin firma del cliente" lo perdía en
      // silencio, y oficina acababa recibiendo el aviso de "Cobrado sin DNI"
      // aunque el técnico sí lo hubiera capturado. El DNI es para la
      // factura, no depende de si hay firma.
      dniCliente:      dniCliente.trim() || null,
      motivoSinFirma:  outcome === 'cobrada_sin_firma' ? motivoSinFirma.trim() : null,
      estadoAnteriorId: order['document-state-id'] || null,
      horaLlegada:     horaLlegada || null,
      horaFin:         horaFinFinal,
      fotos:           fotos.length ? fotos.map(f => f.url) : null,
      clientRequestId: requestIdRef.current,
      contratoMantenimientoResultado: outcome === 'puesta_marcha' ? contratoMantenimientoResultado : null,
      checklistTipo:      outcome === 'puesta_marcha' ? checklistTipo : null,
      checklistItems:     outcome === 'puesta_marcha' ? checklistItems : null,
      checklistFavorable: outcome === 'puesta_marcha' ? checklistFavorable : null,
    };

    // Estado optimista para la UI — coincide con lo que devolvería el servidor.
    const optimista = {
      newStateId:  RESULTADO_STATE[resultado] ?? null,
      signed:      SIGNED_ESTADOS.has(resultado),
      totalAmount: Array.isArray(lineasToSend) && lineasToSend.length > 0
        ? lineasToSend.reduce((s, l) => s + (Number(l.cantidad) || 1) * (Number(l.precioUnitario) || 0), 0)
        : null,
      'payment-option-id': formaPagoToSend === 'tarjeta' ? 156786 : (formaPagoToSend === 'efectivo' ? 53268 : null),
      observaciones: outcome === 'garantia' ? notas : notasGeneral,
    };

    const duracionSegundos = Math.round((Date.now() - aberturaRef.current) / 1000);
    const url = '/.netlify/functions/finalizarVisita';
    const encolar = () => {
      enqueue({ kind: 'finalizarVisita', url, body: payload, clientRequestId: requestIdRef.current });
      trackEvent('orden_cerrada', { orden_id: ordenId, resultado, duracionSegundos, queued: true });
      if (esCorreccion) trackEvent('visita_corregida', { orden_id: ordenId, resultado, queued: true });
      onSuccess({ ...optimista, queued: true });
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        encolar();
        return;
      }

      let resp;
      try {
        resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify(payload),
        });
      } catch (netErr) {
        // La red se cayó justo al enviar — no llegó al servidor: a la cola.
        encolar();
        return;
      }

      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      if (data.stelUpdated === false) {
        console.warn('[finalizarVisita] StelOrder no actualizado — sincronización pendiente');
      }
      // Operaciones secundarias (stock, catálogo de piezas...) que fallaron en
      // el servidor sin bloquear el cierre de la visita — nunca se le muestra
      // nada al técnico por esto, solo queda en PostHog para que oficina lo vea.
      if (Array.isArray(data.avisos)) {
        for (const aviso of data.avisos) {
          trackEvent('error_app', { source: aviso.tipo, message: aviso.mensaje, orden_id: ordenId });
        }
      }

      trackEvent('orden_cerrada', { orden_id: ordenId, resultado, duracionSegundos, queued: false });
      if (esCorreccion) trackEvent('visita_corregida', { orden_id: ordenId, resultado, queued: false });
      onSuccess({
        newStateId:   data.newStateId ?? optimista.newStateId,
        signed:       data.signed ?? optimista.signed,
        totalAmount:  data.totalAmount ?? optimista.totalAmount,
        'payment-option-id': optimista['payment-option-id'],
        observaciones: optimista.observaciones,
        stelSyncFailed: data.stelUpdated === false,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40" onClick={handleRequestClose}>
      <div
        className="bg-white rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sticky top-0 bg-white z-10">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b sticky top-5 bg-white z-10">
          {(step === 'details' || step === 'firma' || step === 'checklist') && (
            <button data-attr="visita-modal-atras" onClick={handleBack} className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h2 className="font-bold text-gray-900 flex-1 text-base">
            {step === 'outcome'
              ? 'Finalizar visita'
              : step === 'checklist'
              ? 'Checklist de puesta en marcha'
              : step === 'firma'
              ? 'Firma del cliente'
              : `${esCorreccion ? 'Corrigiendo: ' : ''}${selectedOutcome?.emoji} ${selectedOutcome?.label}`}
          </h2>
          <button data-attr="visita-modal-cerrar" onClick={handleRequestClose} className="p-1.5 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 pb-10 space-y-5">

          {/* ── Paso 1: elegir resultado ── */}
          {step === 'outcome' && (
            <div className="space-y-2.5">
              <p className="text-sm text-gray-500">¿Cómo quedó la visita?</p>
              {outcomesPrincipales.map(o => (
                <OutcomeButton key={o.key} outcome={o} onSelect={handleSelectOutcome} />
              ))}

              {outcomesSecundarias.length > 0 && (
                mostrarMasOpciones ? (
                  <div className="space-y-2.5 pt-1">
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Otras opciones</p>
                    {outcomesSecundarias.map(o => (
                      <OutcomeButton key={o.key} outcome={o} onSelect={handleSelectOutcome} />
                    ))}
                  </div>
                ) : (
                  <button data-attr="resultado-ver-mas"
                    type="button"
                    onClick={() => setMostrarMasOpciones(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-3 text-sm font-semibold text-gray-500 hover:text-gray-700"
                  >
                    Ver {outcomesSecundarias.length} opciones más
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )
              )}
            </div>
          )}

          {/* ── Paso 1.5 (solo puesta en marcha): checklist técnico del equipo ── */}
          {step === 'checklist' && (
            <div className="space-y-4">
              {!checklistTipo ? (
                <div className="space-y-2.5">
                  <p className="text-sm text-gray-500">No se detectó el tipo de equipo automáticamente — elígelo para cargar el checklist correcto:</p>
                  {Object.entries(CHECKLISTS_PM).map(([key, def]) => (
                    <button data-attr={`checklist-pm-tipo-${key}`}
                      key={key}
                      onClick={() => { setChecklistTipo(key); setChecklistItems(construirChecklistItems(key)); }}
                      className="w-full flex items-center justify-between gap-3 p-3.5 rounded-2xl border-2 border-gray-200 bg-white text-left hover:border-rose-300 transition-all"
                    >
                      <span className="font-semibold text-sm text-gray-800">{def.label}</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 bg-rose-50 border border-rose-200 rounded-xl px-3.5 py-2.5">
                    <div>
                      <p className="text-xs font-bold text-rose-800">{CHECKLISTS_PM[checklistTipo]?.label}</p>
                      {CHECKLISTS_PM[checklistTipo]?.provisional && (
                        <p className="text-[11px] text-rose-500">Provisional — pendiente de confirmar con la hoja oficial</p>
                      )}
                    </div>
                    <button data-attr="checklist-pm-cambiar-tipo"
                      onClick={() => { setChecklistTipo(null); setChecklistItems([]); }}
                      className="text-xs text-rose-600 hover:text-rose-800 underline flex-shrink-0"
                    >
                      Cambiar tipo
                    </button>
                  </div>

                  <p className="text-xs text-gray-400">
                    {checklistItems.filter(i => i.checked).length} de {checklistItems.length} verificados — sigue la hoja de Ariston para los valores exactos de cada medida.
                  </p>

                  {CHECKLISTS_PM[checklistTipo]?.secciones.map(sec => (
                    <div key={sec.titulo} className="space-y-1">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider px-0.5">{sec.titulo}</p>
                      <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50 overflow-hidden">
                        {checklistItems.filter(i => i.seccion === sec.titulo).map(item => (
                          <button data-attr="checklist-pm-item"
                            key={item.id}
                            onClick={() => setChecklistItems(prev => prev.map(i => i.id === item.id ? { ...i, checked: !i.checked } : i))}
                            className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors ${item.checked ? 'bg-emerald-50/50' : 'hover:bg-gray-50'}`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${item.checked ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                              {item.checked && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className={`flex-1 text-sm leading-snug ${item.checked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="space-y-1.5 pt-1">
                    <p className="text-sm font-bold text-gray-700">¿La puesta en marcha queda favorable? <span className="text-red-500">*</span></p>
                    <div className="grid grid-cols-2 gap-3">
                      <button data-attr="checklist-pm-favorable"
                        onClick={() => setChecklistFavorable(true)}
                        className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all ${
                          checklistFavorable === true ? 'border-green-400 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-600'
                        }`}
                      >
                        <span className="text-2xl">✅</span>
                        <span className="text-sm">Favorable</span>
                      </button>
                      <button data-attr="checklist-pm-no-favorable"
                        onClick={() => setChecklistFavorable(false)}
                        className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all ${
                          checklistFavorable === false ? 'border-red-400 bg-red-50 text-red-800' : 'border-gray-200 bg-white text-gray-600'
                        }`}
                      >
                        <span className="text-2xl">⚠️</span>
                        <span className="text-sm">No favorable</span>
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm whitespace-pre-line">{error}</div>
                  )}

                  <button data-attr="checklist-pm-continuar"
                    onClick={() => {
                      if (checklistFavorable === null) { setError('Indica si la puesta en marcha queda favorable o no'); return; }
                      setError('');
                      setStep('details');
                    }}
                    className="w-full py-4 rounded-2xl font-bold text-white text-base bg-rose-600 hover:bg-rose-700 transition-all active:scale-[0.98]"
                  >
                    Continuar
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Paso 2: detalles ── */}
          {/* ── Paso 3: firma del cliente ── */}
          {step === 'firma' && (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-4 text-center space-y-1">
                <p className="font-bold text-blue-900 text-sm">Entrega el teléfono al cliente</p>
                <p className="text-xs text-blue-600">El cliente introduce su DNI y firma para confirmar la visita</p>
              </div>

              {/* DNI del cliente — solo obligatorio si se cobra más de 1000€ o
                  es una instalación (Rafa, 2026-10-16); en el resto de visitas
                  es opcional, para no pedirlo sin necesidad real. */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  DNI / NIE del cliente {requiereDni
                    ? <span className="text-red-500">*</span>
                    : <span className="text-gray-400 font-normal text-xs">(opcional)</span>}
                </label>
                <input
                  type="text"
                  value={dniCliente}
                  onChange={e => setDniCliente(e.target.value.toUpperCase())}
                  placeholder="Ej: 12345678A"
                  maxLength={12}
                  className="w-full text-sm font-mono tracking-widest bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-blue-200 uppercase"
                  autoFocus
                />
              </div>

              {/* Firma */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  Firma <span className="text-red-500">*</span>
                </label>
                <SignatureCanvas onSign={setFirmaBase64} key="firma-canvas" />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm whitespace-pre-line">
                  {error}
                </div>
              )}

              <button data-attr="firma-confirmar"
                onClick={() => {
                  if (requiereDni && !dniCliente.trim()) { return; }
                  trackEvent('orden_paso_completado', { orden_id: ordenId, paso: 'firma' });
                  handleConfirm(firmaBase64);
                }}
                disabled={saving || !firmaBase64 || (requiereDni && !dniCliente.trim())}
                className="w-full flex items-center justify-center gap-2 py-4 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-40 rounded-2xl font-bold text-white text-base transition-all active:scale-[0.98]"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Confirmar firma
                  </>
                )}
              </button>

              {/* Antes era "Continuar sin firma del cliente" — confirmaba sin
                  firma sin pedir ningún motivo ni avisar a oficina. Ahora pasa
                  por el mismo resultado "cobrada_sin_firma" de siempre (motivo
                  obligatorio, oficina se entera) en vez de ser un escape
                  silencioso — se descubre aquí, no se elige de antemano
                  (Rafa, 2026-10-15: "siempre tiene que haber firma, no
                  entiendo que haya un caso concreto para eso... se podría
                  solucionar con un botón en la firma"). */}
              <button data-attr="firma-no-firma"
                type="button"
                onClick={() => { setOutcome('cobrada_sin_firma'); setStep('details'); }}
                disabled={saving}
                className="w-full py-2.5 text-sm text-gray-400 hover:text-gray-600 disabled:opacity-40 transition-colors"
              >
                El cliente no puede o no quiere firmar
              </button>
            </div>
          )}

          {step === 'details' && (
            <>
              {esCorreccion && (
                <div className="flex items-start gap-2.5 bg-gray-100 border border-gray-200 rounded-xl px-4 py-3">
                  <span className="text-lg flex-shrink-0">✏️</span>
                  <p className="text-xs text-gray-600">
                    Estás corrigiendo una visita ya finalizada — los datos de abajo son los que se guardaron antes. Al confirmar, se sobrescriben con lo que dejes aquí.
                  </p>
                </div>
              )}

              {/* ═══ Tiempos de visita (todos los outcomes) ═══════════════════ */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hora llegada</label>
                  <input type="time" value={horaLlegada} onChange={e => setHoraLlegada(e.target.value)}
                    className="w-full text-sm bg-white border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                </div>
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Hora fin</label>
                  <input type="time" value={horaFin} onChange={e => setHoraFin(e.target.value)}
                    className="w-full text-sm bg-white border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200" />
                </div>
              </div>

              {/* ═══ Fotos de la visita (todos los outcomes) ══════════════════ */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Fotos {(outcome === 'garantia' || outcome === 'pendiente_sustitucion')
                      ? <span className="text-red-500 normal-case">(obligatorias para {outcome === 'garantia' ? 'garantía' : 'sustitución'})</span>
                      : <span className="text-gray-400 normal-case font-normal">(opcional)</span>}
                  </label>
                  {subiendoFotos > 0 && <span className="text-xs text-gray-400">Subiendo {subiendoFotos}…</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {fotos.map((f, i) => (
                    <div key={f.url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                      <img src={f.url} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                      <button data-attr="foto-quitar" type="button" onClick={() => setFotos(prev => prev.filter(x => x.url !== f.url))}
                        className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white text-xs leading-none">×</button>
                    </div>
                  ))}
                  <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 cursor-pointer hover:border-blue-300 hover:text-blue-500 transition-colors">
                    <span className="text-2xl leading-none">＋</span>
                    <span className="text-xs">Cámara</span>
                    <input type="file" accept="image/*" capture="environment" multiple hidden onChange={handleFotos} />
                  </label>
                </div>
                {fotoError && <p className="text-xs text-amber-600">{fotoError}</p>}
              </div>

              {/* ═══ COBRADA ═══════════════════════════════════════════════════ */}
              {outcome === 'cobrada' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Descripción del trabajo realizado <span className="text-red-500">*</span></label>
                    <textarea
                      value={notasGeneral}
                      onChange={e => setNotasGeneral(e.target.value)}
                      placeholder="Describe qué se ha hecho en la visita..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-300 resize-none"
                    />
                  </div>

                  {esMantenimientoSepa && (
                    <div className="flex items-start gap-2.5 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                      <span className="text-lg flex-shrink-0">🏦</span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-teal-800">Este contrato se cobra por domiciliación{order.mantenimientoPack ? ` (${order.mantenimientoPack})` : ''}</p>
                        <p className="text-xs text-teal-700 mt-0.5">No hace falta cobrar nada en esta visita — el cargo lo hace el banco.</p>
                        <label className="flex items-center gap-2 mt-2 text-xs text-teal-800 cursor-pointer">
                          <input type="checkbox" checked={cobroExtra} onChange={e => setCobroExtra(e.target.checked)} />
                          He cobrado algo aparte, no incluido en el contrato
                        </label>
                      </div>
                    </div>
                  )}

                  {(!esMantenimientoSepa || cobroExtra) && (
                    <>
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-gray-700">Servicios y materiales</p>
                        <p className="text-xs text-gray-400">El total se calcula automáticamente de las líneas</p>
                      </div>
                      <LineBuilder lines={lines} setLines={setLines} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />

                      <div className="space-y-1.5">
                        <p className="text-sm font-semibold text-gray-700">Forma de pago</p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: 'efectivo', label: 'Efectivo', emoji: '💵' },
                            { id: 'tarjeta',  label: 'Tarjeta',  emoji: '💳' },
                          ].map(fp => (
                            <button data-attr={`cobro-${fp.id}`}
                              key={fp.id}
                              onClick={() => { registrarCobro(fp.id); setFormaPago(fp.id); }}
                              className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                                formaPago === fp.id
                                  ? 'border-green-400 bg-green-50 text-green-800'
                                  : 'border-gray-200 bg-white text-gray-600'
                              }`}
                            >
                              <span className="text-xl">{fp.emoji}</span>
                              {fp.label}
                            </button>
                          ))}
                          {/* No es una forma de pago real — cambia el resultado a
                              "pendiente_pago" (trabajo terminado, factura a cobrar
                              luego por oficina). Antes era un botón aparte en la
                              lista inicial; se mueve aquí porque es, en el fondo,
                              una variante de "cómo se cobra esto" (Rafa, 2026-10-15). */}
                          <button data-attr="cobro-pendiente"
                            type="button"
                            onClick={() => {
                              registrarCobro('transferencia');
                              setOutcome('pendiente_pago');
                              setJustificacion(prev => prev || notasGeneral);
                            }}
                            className="flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all border-gray-200 bg-white text-gray-600"
                          >
                            <span className="text-xl">🧾</span>
                            Pendiente
                          </button>
                        </div>
                        <p className="text-xs text-gray-400">Las transferencias las gestiona la oficina</p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ═══ COBRADA SIN FIRMA ════════════════════════════════════════ */}
              {outcome === 'cobrada_sin_firma' && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
                    La oficina será notificada para verificar el cobro con el cliente.
                  </div>
                  {esMantenimientoSepa && (
                    <div className="flex items-start gap-2.5 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                      <span className="text-lg flex-shrink-0">🏦</span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-teal-800">Este contrato se cobra por domiciliación{order.mantenimientoPack ? ` (${order.mantenimientoPack})` : ''}</p>
                        <p className="text-xs text-teal-700 mt-0.5">No hace falta cobrar nada en esta visita — el cargo lo hace el banco.</p>
                        <label className="flex items-center gap-2 mt-2 text-xs text-teal-800 cursor-pointer">
                          <input type="checkbox" checked={cobroExtra} onChange={e => setCobroExtra(e.target.checked)} />
                          He cobrado algo aparte, no incluido en el contrato
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-gray-700">Servicios y materiales</p>
                    <p className="text-xs text-gray-400">El total se calcula automáticamente de las líneas</p>
                  </div>
                  <LineBuilder lines={lines} setLines={setLines} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />
                  {(!esMantenimientoSepa || cobroExtra) && (
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold text-gray-700">Forma de pago</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'efectivo', label: 'Efectivo', emoji: '💵' },
                        { id: 'tarjeta',  label: 'Tarjeta',  emoji: '💳' },
                      ].map(fp => (
                        <button data-attr={`cobro-${fp.id}`} key={fp.id} onClick={() => { registrarCobro(fp.id); setFormaPago(fp.id); }}
                          className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                            formaPago === fp.id ? 'border-green-400 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-600'
                          }`}>
                          <span className="text-xl">{fp.emoji}</span>{fp.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Motivo de ausencia de firma <span className="text-red-500">*</span></label>
                    <textarea value={motivoSinFirma} onChange={e => setMotivoSinFirma(e.target.value)}
                      placeholder="Ej: Cliente mayor sin posibilidad de firmar, cliente ausente al terminar, pago por transferencia confirmada..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 resize-none"
                      autoFocus />
                  </div>
                </div>
              )}

              {/* ═══ PENDIENTE PAGO ════════════════════════════════════════════ */}
              {outcome === 'pendiente_pago' && (
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-gray-700">Servicios realizados</p>
                      <p className="text-xs text-gray-400">Registra el trabajo para que la oficina gestione el cobro</p>
                    </div>
                    <button data-attr="cobro-volver-a-cobro-normal" type="button" onClick={() => setOutcome('cobrada')} className="text-xs text-gray-400 underline flex-shrink-0 mt-0.5">
                      ← Es un cobro normal
                    </button>
                  </div>
                  <LineBuilder lines={lines} setLines={setLines} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />

                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Justificación del pago pendiente <span className="text-red-500">*</span></label>
                    <textarea
                      value={justificacion}
                      onChange={e => setJustificacion(e.target.value)}
                      placeholder="Ej: Cliente ausente al terminar, queda pendiente de confirmación de transferencia..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
                      autoFocus
                    />
                  </div>
                </div>
              )}

              {/* ═══ GARANTÍA ══════════════════════════════════════════════════ */}
              {outcome === 'garantia' && garantiaCubierta === null && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-700">¿Está cubierto por la garantía?</p>
                  <button data-attr="garantia-cubierta-si"
                    onClick={() => { trackEvent('garantia_respuesta', { orden_id: ordenId, cubierto: 'si' }); setGarantiaCubierta(true); }}
                    className="w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 border-teal-300 bg-teal-50 text-teal-800 text-left transition-all active:scale-[0.98]"
                  >
                    <span className="text-2xl leading-none">🛡️</span>
                    <span className="font-semibold text-sm">Sí, está cubierto</span>
                  </button>
                  <button data-attr="garantia-cubierta-no"
                    onClick={handleGarantiaNoCubierta}
                    className="w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 border-gray-300 bg-gray-50 text-gray-700 text-left transition-all active:scale-[0.98]"
                  >
                    <span className="text-2xl leading-none">💰</span>
                    <span className="font-semibold text-sm">No — el cliente debe abonar la visita</span>
                  </button>
                </div>
              )}
              {outcome === 'garantia' && garantiaCubierta === true && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Marca del equipo <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-3 gap-2">
                      {MARCAS.map(m => (
                        <button data-attr={`garantia-marca-${m.key}`}
                          key={m.key}
                          onClick={() => setMarcaKey(m.key)}
                          className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                            marcaKey === m.key
                              ? 'border-teal-400 bg-teal-50 text-teal-800'
                              : 'border-gray-200 bg-white text-gray-600'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Descripción del fallo (opcional)</label>
                    <textarea
                      value={notas}
                      onChange={e => setNotas(e.target.value)}
                      placeholder="Describe el fallo encontrado y la reparación realizada..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"
                    />
                  </div>

                  {/* Nº de serie: solo se pide si StelOrder no lo tiene ya —
                      si el activo ya trae uno, no se vuelve a preguntar. */}
                  {faltaSerieGarantia && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-gray-700">Número de serie del equipo <span className="text-red-500">*</span></label>
                      <p className="text-xs text-gray-400">No está registrado en StelOrder — sin él no se puede tramitar la garantía con el fabricante.</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={numeroSerieCapturado}
                          onChange={e => { setNumeroSerieCapturado(e.target.value); serieOrigenRef.current = 'manual'; }}
                          onBlur={() => {
                            if (serieOrigenRef.current === 'manual' && numeroSerieCapturado.trim()) {
                              trackEvent('serie_tecleado', { marca: marcaKey || null });
                              serieOrigenRef.current = null;
                            }
                          }}
                          placeholder={marcaKey === 'ariston' ? 'Ej: 363914735250460001676 (21 dígitos)' : 'Ej: AR202600123'}
                          className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-teal-300"
                        />
                        <button data-attr="garantia-escanear-serie"
                          type="button"
                          onClick={() => setShowSerieScanner(true)}
                          title="Escanear la pegatina del equipo"
                          className="flex-shrink-0 px-3.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-600 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                        </button>
                      </div>
                      {marcaKey === 'ariston' && numeroSerieCapturado.trim() && !serieAristonValida(numeroSerieCapturado) && (
                        <p className="text-xs text-amber-600">Ariston usa números de serie de 21 dígitos — revisa que esté completo.</p>
                      )}
                      {showSerieScanner && (
                        <BarcodeScanner
                          label="Apunta a la pegatina del número de serie"
                          placeholderManual="O escribe el número de serie manualmente"
                          onScan={(code) => {
                            setNumeroSerieCapturado(code);
                            serieOrigenRef.current = null;
                            trackEvent('serie_escaneado', { marca: marcaKey || null });
                            setShowSerieScanner(false);
                          }}
                          onClose={() => setShowSerieScanner(false)}
                        />
                      )}
                    </div>
                  )}

                  {/* Piezas sustituidas en garantía: no las paga el cliente
                      (payment-option-id va a PAYMENT_WARRANTY en el backend,
                      ver finalizarVisita.js) — se usan para descontar stock,
                      alimentar el catálogo por modelo, y son las mismas
                      líneas que luego lee crearAlbaranAriston.js al liquidar
                      con el fabricante. Opcional: no todas las garantías
                      cambian una pieza (puede ser solo mano de obra). */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Piezas sustituidas (opcional)</label>
                    <LineBuilder lines={lines} setLines={setLines} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />
                  </div>
                </div>
              )}

              {/* ═══ FALTA MATERIAL ════════════════════════════════════════════ */}
              {outcome === 'pendiente_material' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Material que falta <span className="text-red-500">*</span></label>
                    <ProductoSelector items={materialItems} setItems={setMaterialItems} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />
                  </div>

                  {/* Tip IA */}
                  <div className="flex items-start gap-2.5 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                    <span className="text-lg flex-shrink-0 mt-0.5">🤖</span>
                    <div>
                      <p className="text-xs font-bold text-violet-800">¿No sabes qué pieza es?</p>
                      <p className="text-xs text-violet-700 mt-0.5">
                        Cierra este panel y usa el <strong>chat IA</strong> de la orden para preguntar por la referencia del material.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Observaciones (opcional)</label>
                    <textarea
                      value={notasGeneral}
                      onChange={e => setNotasGeneral(e.target.value)}
                      placeholder="Ej: La sonda está descontinuada, buscar equivalente..."
                      rows={2}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
                    />
                  </div>

                  {/* "¿Se cobra la visita?" solo fuera de garantía — una
                      garantía nunca la paga el cliente, la gestiona el
                      fabricante (Rafa, 2026-10-30). Fuera de garantía, el
                      técnico puede cobrar el desplazamiento/diagnóstico de
                      hoy aunque haga falta una segunda visita con la pieza;
                      la orden sigue en Pendiente de material para esa
                      segunda visita. */}
                  {!isGarantia && (
                    <div className="space-y-3 border-t pt-4">
                      <div className="space-y-1.5">
                        <p className="text-sm font-bold text-gray-700">¿Se cobra la visita de hoy? <span className="text-red-500">*</span></p>
                        <div className="grid grid-cols-2 gap-3">
                          <button data-attr="cobrar-visita-si"
                            onClick={() => setCobrarVisita(true)}
                            className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all ${
                              cobrarVisita === true
                                ? 'border-green-400 bg-green-50 text-green-800'
                                : 'border-gray-200 bg-white text-gray-600'
                            }`}
                          >
                            <span className="text-2xl">✅</span>
                            <span className="text-sm">Sí, se cobra</span>
                          </button>
                          <button data-attr="cobrar-visita-no"
                            onClick={() => { registrarCobro('no_se_cobra'); setCobrarVisita(false); }}
                            className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all ${
                              cobrarVisita === false
                                ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                                : 'border-gray-200 bg-white text-gray-600'
                            }`}
                          >
                            <span className="text-2xl">🆓</span>
                            <span className="text-sm">No se cobra</span>
                          </button>
                        </div>
                      </div>

                      {cobrarVisita === true && (
                        <div className="space-y-3 border-t pt-4">
                          <div className="space-y-1">
                            <p className="text-sm font-bold text-gray-700">Servicios de la visita</p>
                            <p className="text-xs text-gray-400">Desplazamiento/diagnóstico de hoy — la segunda visita con el material se cobrará aparte</p>
                          </div>
                          <LineBuilder lines={lines} setLines={setLines} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            {[
                              { id: 'efectivo', label: 'Efectivo', emoji: '💵' },
                              { id: 'tarjeta',  label: 'Tarjeta',  emoji: '💳' },
                            ].map(fp => (
                              <button data-attr={`cobro-${fp.id}`}
                                key={fp.id}
                                onClick={() => { registrarCobro(fp.id); setFormaPago(fp.id); }}
                                className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                                  formaPago === fp.id
                                    ? 'border-green-400 bg-green-50 text-green-800'
                                    : 'border-gray-200 bg-white text-gray-600'
                                }`}
                              >
                                <span>{fp.emoji}</span> {fp.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ═══ AUSENTE ═══════════════════════════════════════════════════ */}
              {outcome === 'ausente' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
                    Se notificará al cliente y se reprogramará la visita desde la oficina.
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Observaciones <span className="text-red-500">*</span></label>
                    <textarea
                      value={notasGeneral}
                      onChange={e => setNotasGeneral(e.target.value)}
                      placeholder="Ej: Nadie en casa. Dejé nota en buzón. Llamé al teléfono sin respuesta..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
                      autoFocus
                    />
                  </div>
                </div>
              )}

              {/* ═══ PUESTA EN MARCHA ══════════════════════════════════════════ */}
              {outcome === 'puesta_marcha' && (
                <div className="space-y-4">
                  <div className={`flex items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${
                    checklistFavorable ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}>
                    <span>{checklistFavorable ? '✅ Checklist favorable' : '⚠️ Checklist no favorable'} — {CHECKLISTS_PM[checklistTipo]?.label || 'equipo'}</span>
                    <button data-attr="puesta-marcha-revisar-checklist" onClick={() => setStep('checklist')} className="text-xs underline flex-shrink-0 opacity-70 hover:opacity-100">Revisar</button>
                  </div>

                  <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800">
                    Si todavía no le has ofrecido el contrato de mantenimiento, hazlo desde el botón
                    <strong> "Crear contrato de mantenimiento"</strong> en la ficha de la orden antes de cerrar —
                    aquí solo se registra el resultado de esa conversación.
                  </div>

                  <div className="space-y-1.5">
                    <p className="text-sm font-bold text-gray-700">¿Aceptó el contrato de mantenimiento? <span className="text-red-500">*</span></p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'aceptado',   label: 'Sí, aceptó', emoji: '✅', ring: 'border-green-400 bg-green-50 text-green-800' },
                        { id: 'rechazado',  label: 'No quiso',   emoji: '❌', ring: 'border-red-400 bg-red-50 text-red-800' },
                        { id: 'no_ofrecido', label: 'No lo ofrecí', emoji: '➖', ring: 'border-gray-400 bg-gray-100 text-gray-800' },
                      ].map(opt => (
                        <button data-attr={`contrato-resultado-${opt.id}`}
                          key={opt.id}
                          onClick={() => setContratoMantenimientoResultado(opt.id)}
                          className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 font-semibold transition-all text-center ${
                            contratoMantenimientoResultado === opt.id ? opt.ring : 'border-gray-200 bg-white text-gray-600'
                          }`}
                        >
                          <span className="text-xl">{opt.emoji}</span>
                          <span className="text-xs leading-tight">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">Onboarding realizado <span className="text-red-500">*</span></label>
                    <textarea
                      value={notasGeneral}
                      onChange={e => setNotasGeneral(e.target.value)}
                      placeholder="Ej: Expliqué el funcionamiento del equipo y el termostato, resolví dudas sobre consumo..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-rose-300 resize-none"
                      autoFocus
                    />
                  </div>
                </div>
              )}

              {/* ═══ PRESUPUESTO ═══════════════════════════════════════════════ */}
              {outcome === 'pendiente_presupuesto' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <p className="text-sm font-bold text-gray-700">¿Se cobra la visita? <span className="text-red-500">*</span></p>
                    <div className="grid grid-cols-2 gap-3">
                      <button data-attr="cobrar-visita-si"
                        onClick={() => setCobrarVisita(true)}
                        className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all ${
                          cobrarVisita === true
                            ? 'border-green-400 bg-green-50 text-green-800'
                            : 'border-gray-200 bg-white text-gray-600'
                        }`}
                      >
                        <span className="text-2xl">✅</span>
                        <span className="text-sm">Sí, se cobra</span>
                      </button>
                      <button data-attr="cobrar-visita-no"
                        onClick={() => { registrarCobro('no_se_cobra'); setCobrarVisita(false); }}
                        className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all ${
                          cobrarVisita === false
                            ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                            : 'border-gray-200 bg-white text-gray-600'
                        }`}
                      >
                        <span className="text-2xl">🆓</span>
                        <span className="text-sm">No se cobra</span>
                      </button>
                    </div>
                  </div>

                  {cobrarVisita === true && (
                    <div className="space-y-3 border-t pt-4">
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-gray-700">Servicios de la visita</p>
                        <p className="text-xs text-gray-400">Se registrará como cobrado y luego la oficina creará el presupuesto</p>
                      </div>
                      <LineBuilder lines={visitaLines} setLines={setVisitaLines} ordenId={ordenId} activoId={order.activoId || order.activoIds?.[0]} />
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        {[
                          { id: 'efectivo', label: 'Efectivo', emoji: '💵' },
                          { id: 'tarjeta',  label: 'Tarjeta',  emoji: '💳' },
                        ].map(fp => (
                          <button data-attr={`cobro-visita-${fp.id}`}
                            key={fp.id}
                            onClick={() => { registrarCobro(fp.id); setVisitaFormaPago(fp.id); }}
                            className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                              visitaFormaPago === fp.id
                                ? 'border-green-400 bg-green-50 text-green-800'
                                : 'border-gray-200 bg-white text-gray-600'
                            }`}
                          >
                            <span>{fp.emoji}</span> {fp.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {cobrarVisita === false && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 text-sm text-indigo-800">
                      La oficina recibirá la notificación y creará el presupuesto para este cliente.
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">
                      Notas para el presupuesto{cobrarVisita === false ? <span className="text-red-500"> *</span> : <span className="text-gray-400"> (opcional)</span>}
                    </label>
                    <textarea
                      value={notasGeneral}
                      onChange={e => setNotasGeneral(e.target.value)}
                      placeholder="Describe el trabajo a presupuestar, materiales estimados..."
                      rows={3}
                      className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* ═══ SUSTITUCIÓN ═══════════════════════════════════════════════ */}
              {outcome === 'pendiente_sustitucion' && (
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 text-sm text-purple-800">
                    Los datos del nuevo equipo quedarán registrados en la orden para que la oficina actualice la ficha del cliente.
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-gray-700">Marca del nuevo equipo <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={nuevoMarca}
                        onChange={e => setNuevoMarca(e.target.value)}
                        placeholder="Ej: Ariston, Daikin, Saunier..."
                        className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-purple-300"
                        autoFocus
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-gray-700">Modelo <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={nuevoModelo}
                        onChange={e => setNuevoModelo(e.target.value)}
                        placeholder="Ej: NUOS Evo A 80V, Altherma 3R 8kW..."
                        className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-gray-700">Número de serie (opcional)</label>
                      <input
                        type="text"
                        value={nuevoSerie}
                        onChange={e => setNuevoSerie(e.target.value)}
                        placeholder="Ej: AR202600123"
                        className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-gray-700">Tipo de equipo (opcional)</label>
                      <select
                        value={nuevoTipo}
                        onChange={e => setNuevoTipo(e.target.value)}
                        className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      >
                        <option value="">Seleccionar tipo...</option>
                        {TIPOS_EQUIPO.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-gray-700">Observaciones (opcional)</label>
                      <textarea
                        value={nuevoNotas}
                        onChange={e => setNuevoNotas(e.target.value)}
                        placeholder="Motivo de la sustitución, condiciones especiales de instalación..."
                        rows={2}
                        className="w-full text-sm text-gray-700 placeholder-gray-400 bg-gray-50 rounded-xl px-3.5 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm whitespace-pre-line">
                  {error}
                </div>
              )}

              {/* Botón — avanza al paso de firma (o confirma directamente si es ausente) */}
              <button data-attr="visita-confirmar"
                onClick={handleGoToFirma}
                disabled={saving}
                className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white text-base transition-all active:scale-[0.98] disabled:opacity-50 ${
                  outcome === 'cobrada' || (outcome === 'pendiente_presupuesto' && cobrarVisita) || (outcome === 'pendiente_material' && cobrarVisita)
                    ? 'bg-green-600 hover:bg-green-700'
                    : outcome === 'cobrada_sin_firma'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : outcome === 'ausente'
                    ? 'bg-gray-600 hover:bg-gray-700'
                    : outcome === 'garantia'
                    ? 'bg-teal-600 hover:bg-teal-700'
                    : outcome === 'pendiente_material'
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : outcome === 'puesta_marcha'
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : outcome === 'ausente' ? (
                  <>{selectedOutcome?.emoji}&nbsp;Confirmar ausencia</>
                ) : outcome === 'cobrada_sin_firma' ? (
                  <>{selectedOutcome?.emoji}&nbsp;Registrar cobro sin firma</>
                ) : outcome === 'garantia' || outcome === 'pendiente_sustitucion' || outcome === 'puesta_marcha' ||
                    (outcome === 'pendiente_material' && !cobrarVisita) ||
                    (outcome === 'pendiente_presupuesto' && !cobrarVisita) ? (
                  <>{selectedOutcome?.emoji}&nbsp;Confirmar y registrar</>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    {(outcome === 'pendiente_presupuesto' || outcome === 'pendiente_material') && cobrarVisita === true
                      ? 'Cobrar visita — solicitar firma'
                      : 'Continuar — solicitar firma'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
